import type { TranslationKey } from './types';

export const en: Record<TranslationKey, string> = {
    // Common
    'common.login': 'Log in',
    'common.logout': 'Log out',
    'common.save': 'Save',
    'common.saving': 'Saving…',
    'common.saved': 'Saved',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.deleting': 'Deleting…',
    'common.edit': 'Edit',
    'common.loading': 'Loading…',
    'common.error': 'Error',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.finish': 'Finish',
    'common.search': 'Search',
    'common.copy': 'Copy',
    'common.copied': 'Copied',
    'common.open': 'Open',
    'common.confirm': 'Confirm',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.close': 'Close',
    'common.retry': 'Retry',
    'common.language': 'Language',

    'games.form_title': 'New game',
    'games.field_date': 'Date and time',
    'games.field_format': 'Format',
    'games.field_host': 'Host',
    'games.field_map': 'Map',
    'games.field_mod': 'Mod',
    'games.lobby_settings': 'Lobby settings',
    'games.field_duration': 'Duration (min, optional)',
    'games.field_players': 'Players',
    'games.field_notes': 'Notes (optional)',
    'games.add_player': '+ Add player',
    'games.auto_places': 'Assign places',
    'games.auto_places_hint': 'Assign places by the order of players in the list (top = 1st place)',
    'games.remove_player': 'Remove',
    'games.move_up': 'Move up',
    'games.move_down': 'Move down',
    'games.winner_label': 'Winner',
    'games.select_placeholder': '— select —',
    'games.no_mod': '— no mod —',
    'games.player_placeholder': 'Player name…',
    'games.error_no_date': 'Please specify date and time',
    'games.error_no_refs': 'Fill in all required fields',
    'games.error_no_players': 'Add at least one player',
    'games.error_no_winner': 'Mark at least one winner',
    'games.save_error': 'Failed to save game: ',
    'games.confirm_close': 'Close the form? Unsaved changes will be lost.',
    'games.player_not_found': 'Player not found',
    'games.error_duplicate_player': 'The same player is selected twice',
    'games.form_title_edit': 'Edit game',
    'games.edit_game': 'Edit',
    'games.delete_game': 'Delete',
    'games.duplicate_settings': 'Add a new game by copying settings from this one',
    'games.delete_confirm': 'Delete game from {date} (winners: {winners})?',
    'games.delete_error': 'Failed to delete: ',
    'games.load_one_error': 'Failed to load game: ',
    'games.field_place': 'Place',
    'games.error_duplicate_place': 'Two players have the same place',
    'games.error_missing_places': 'Not all players have a place assigned',
    'games.error_player_not_selected': 'Select a player from the list: {names}',
    'games.players_hint': 'Check the winner. Assign a place to everyone else (2nd, 3rd, …) — places must be unique.',
    'games.placement_hint_solo': 'Players are ranked by the overall score, excluding the winner.',
    'games.placement_hint_team': 'Players are ranked by the overall score regardless of whether they won.',

    'player.games_title': 'Games',
    'player.open_stats': '📊 Player stats',
    'player.overlay_show': '📺 To OBS overlay',
    'player.overlay_show_hint': 'Show the card in the OBS overlay for 7 seconds in the mode selected here (overlay link is in "Streamer mode").',
    'player.overlay_show_done': '✓ Shown in overlay',
    'player.overlay_show_error': 'Failed to show in overlay:',
    'player.overlay_show_no_obs': 'Shown, but the OBS overlay source is not connected — refresh it (link in Streamer mode).',
    'player.games_all': 'All games',
    'player.games_empty': 'Player has not participated in any games yet',
    'player.games_total': 'Total games',
    'player.games_wins': 'Wins',
    'player.games_winrate': 'Winrate',
    'player.games_favorite_race': 'Favorite race',
    'player.games_favorite_format': 'Favorite format',
    'player.games_races_title': 'Race stats',
    'player.games_games_word': 'games',
    'player.games_wins_word': 'wins',
    'player.games_result_win': 'Win',
    'player.games_load_error': 'Failed to load games',
    'player.games_show_all': 'Show all',
    'player.games_hide_all': 'Hide',
    'player.game_details_title': 'Game details',
    'player.game_details_duration': 'Duration',
    'player.game_details_notes': 'Notes',
    'player.host_title': 'Game host',
    'player.host_games_total': 'Games hosted',
    'player.host_total_players': 'Total players',
    'player.host_avg_players': 'Avg players per game',
    'player.host_last_game': 'Last game',
    'player.host_games_empty': 'The host has not hosted any games yet',
    'player.host_load_error': 'Failed to load host stats',
    'player.host_top_maps': 'Top maps',
    'player.host_top_mods': 'Top mods',
    'player.host_top_players': 'Top players',
    'player.games_tops_title': 'Game stats',
    'player.games_top_maps': 'Top maps',
    'player.games_top_mods': 'Top mods',
    'player.games_top_teammates': 'Top teammates',
    'player.links_title': 'Links',
    'player.link_live': 'live',

    'games.title': 'Games',
    'games.search_placeholder': 'Search by player, map, host…',
    'games.add_button': '+ Add game',
    'games.no_games': 'No games yet',
    'games.nothing_found': 'Nothing found',
    'games.load_error': 'Failed to load games: ',
    'games.load_more': 'Load earlier games',
    'games.loading_more': 'Loading…',
    'games.host_label': 'Host',
    'games.added_by': 'added by',
    'games.edited_at': 'edited',
    'games.created_at': 'created',
    'games.time_ago_just_now': 'just now',
    'games.time_ago_minutes': '{n} min ago',
    'games.time_ago_hours': '{n} hr ago',
    'games.time_ago_days': '{n} d ago',
    'games.minutes_short': 'min',
    'games.new_badge': 'new',
    'games.new_badge_hint': 'This game appeared since your last visit',
    'games.new_banner_title': 'New games since your last visit: {n}',
    'games.new_banner_hint': 'Add reactions and mark who was memorable — just a couple of clicks',
    'games.new_banner_hint_anon': 'Sign in to rate the games',
    'games.new_banner_dismiss': 'Hide',

    // Topbar
    'topbar.league': 'SC2 FFA LEAGUE',
    'topbar.database': '// PLAYER DATABASE',
    'topbar.streamer_mode': 'Streamer mode',
    'topbar.admin': 'Admin',
    'topbar.guest': 'You are not logged in',
    'topbar.role_admin': 'ADMIN',
    'topbar.role_moderator': 'MOD',
    'topbar.role_ghost': 'GHOST',

    // Notifications
    'notif.delete': 'Delete notification',
    'notif.title': 'Notifications',
    'notif.mark_all': 'Mark all as read',
    'notif.empty': 'No notifications yet',
    'notif.load_error': 'Failed to load notifications',
    'notif.anonymous': 'A user',
    'notif.rating_new': '{name} rated you',
    'notif.rating_changed': '{name} changed your rating',
    'notif.player': 'Player: {name}',
    'notif.race': 'race {race}',
    'notif.changed': 'changed: {fields}',
    'notif.field.race': 'race',
    'notif.field.tier': 'tier',
    'notif.role_granted': 'You were granted the {role} role',
    'notif.role_revoked': 'Your {role} role was removed',
    'notif.role_moderator': 'moderator',
    'notif.role_admin': 'admin',
    'notif.role_ghost': 'ghost',
    'notif.player_linked': 'Your account was linked to a player',
    'notif.by': 'by {name}',
    'notif.recap_title': 'Since your last visit',
    'notif.recap_meta': 'New games: {n}',

    'nav.players': 'Players',
    'nav.games': 'Games',
    'nav.maps': 'Maps',
    'nav.mods': 'Mods',
    'nav.stats': 'Stats',

    // Maps section
    'maps.title': 'Maps',
    'maps.search_placeholder': 'Search map…',
    'maps.filter_all': 'All',
    'maps.filter_type': 'Map type',
    'maps.filter_players': 'Players',
    'maps.filter_formats': 'Team format',
    'maps.filter_author': 'Author',
    'maps.filter_all_authors': 'All authors',
    'maps.filter_list': 'List',
    'maps.filter_all_lists': 'All lists',
    'maps.author': 'Author: {name}',
    'maps.link': 'Link',
    'maps.copy_name_hint': 'Click to copy the name',
    'maps.copy_link': 'Copy link',
    'maps.copied': 'Copied!',
    'maps.author_not_found': 'Author not found',
    'maps.players': 'Players: {n}',
    'maps.like': 'Like',
    'maps.dislike': 'Dislike',
    'maps.hover_hint': 'Hover to enlarge',
    'maps.empty': 'No maps found',
    'maps.error': 'Failed to load maps',
    'maps.add_button': 'Add map',
    'maps.form_title': 'New map',
    'maps.form_title_edit': 'Edit map',
    'maps.field_name': 'Name',
    'maps.field_alt': 'Alt name (localization)',
    'maps.field_players': 'Players',
    'maps.field_author': 'Author',
    'maps.field_link': 'Link',
    'maps.field_types': 'Map types',
    'maps.field_formats': 'Team formats',
    'maps.field_image': 'Image',
    'maps.field_unbalanced': 'Unbalanced map',
    'maps.unbalanced_badge': 'Unbalanced',
    'maps.unbalanced_hint': 'Map is marked as unbalanced by a moderator',
    'maps.name_required': 'Enter a map name',
    'maps.link_loading': 'Loading map data from the link…',
    'maps.link_filled': 'Empty fields filled from the link — please check',
    'maps.link_not_found': 'Nothing found for this link — fill in manually',
    'maps.delete_confirm': 'Delete map "{name}"?\n\nGames played on it are kept; an admin can restore the map.',
    'maps.delete_error': 'Failed to delete the map: ',
    'maps.new_error': 'Failed to create map: ',
    'maps.save_error': 'Failed to save map: ',
    'maps.edit_hint': 'Click to edit',

    // Mods section
    'mods.title': 'Mods',
    'mods.search_placeholder': 'Search mod…',
    'mods.filter_author': 'Author',
    'mods.filter_all_authors': 'All authors',
    'mods.author': 'Author: {name}',
    'mods.like': 'Like',
    'mods.dislike': 'Dislike',
    'mods.empty': 'No mods found',
    'mods.error': 'Failed to load mods',
    'mods.add_button': 'Add mod',
    'mods.edit_hint': 'Click to edit',
    'mods.form_title': 'New mod',
    'mods.form_title_edit': 'Edit mod',
    'mods.field_name': 'Name',
    'mods.field_author': 'Author',
    'mods.field_description': 'Description',
    'mods.field_description_placeholder': 'Short description',
    'mods.name_required': 'Enter a mod name',
    'mods.new_error': 'Failed to create mod: ',
    'mods.save_error': 'Failed to save mod: ',
    'mods.autosave_hint': 'Changes are saved automatically as you type — no separate Save button needed.',
    'mods.autosave_pending': 'Not saved',
    'mods.autosave_error': 'Save failed',
    'mods.unsaved_confirm': 'Changes are not saved yet. Close the form without saving?',
    'mods.done': 'Done',
    'mods.delete_confirm': 'Delete mod "{name}"?\n\nGames with this mod: {n} — they will lose the mod mark. An admin can restore the mod with its settings and presets (votes are not kept).',
    'mods.delete_error': 'Failed to delete the mod: ',
    'mods.settings_title': 'Settings',
    'mods.settings_hint': 'The mod settings randomizer uses these parameters. Nested parameters (↳) appear only when the switch parameter has the selected value.',
    'mods.settings_empty': 'No parameters yet',
    'mods.settings_count': 'Parameters: {n}',
    'mods.settings_save_error': 'Mod saved, but settings failed: ',
    'mods.add_param': '+ Parameter',
    'mods.param_name_placeholder': 'Parameter',
    'mods.param_values_placeholder': 'Values, comma-separated',
    'mods.param_default_placeholder': 'Default value',
    'mods.param_default_hint': 'Default value (must be one of the values)',
    'mods.param_default_short': 'default: {value}',
    'mods.param_randomize': 'Random',
    'mods.param_randomize_hint': 'Randomize this parameter by default',
    'mods.param_fixed_short': 'fixed',
    'mods.param_fixed_hint': 'This parameter is not randomized by default',
    'mods.games_count': 'Games: {n}',
    'mods.games_count_hint': 'Games played with this mod',
    'mods.presets_title': 'Settings presets',
    'mods.presets_hint': 'Saved combinations of parameter values.',
    'mods.presets_empty': 'No presets yet',
    'mods.presets_label': 'Presets:',
    'mods.presets_no_params': 'Add parameters with values first',
    'mods.preset_name_placeholder': 'Preset name',
    'mods.add_preset': '+ Preset',
    'mods.delete_preset': 'Delete preset',
    'mods.preset_no_values': 'No values set',
    'mods.delete_param': 'Delete parameter',
    'mods.param_depends_label': 'Branching',
    'mods.param_depends_always': 'none — always active',
    'mods.param_depends_hint': 'Branching: the parameter appears only when the selected parent parameter has one of the checked values',
    'mods.param_depends_short': 'when {param} = {values}',
    'mods.param_depends_values_hint': 'Check the values that activate this parameter',
    'mods.param_add_hint': 'Add a top-level parameter',
    'mods.import_replay': '⤓ From replay',
    'mods.import_replay_hint': 'Take the lobby options from a replay played on this mod (the mod file is downloaded from Blizzard servers)',
    'mods.import_replay_loading': 'Loading…',
    'mods.import_replay_error': 'Could not read the replay: ',
    'mods.import_replay_none': 'The replay has no mod with lobby options (or the mod files could not be downloaded)',
    'mods.import_replay_pick': 'Take the lobby options from "{name}"?',
    'mods.import_replay_replace': 'Replace the parameters with the options from "{name}"?\n\n{params}\n\nYour own names and branching will need to be redone.',
    'mods.param_field_values': 'Values (comma-separated)',
    'mods.param_field_default': 'Default',
    'mods.param_branch_count': 'branches: {n}',
    'mods.param_branch_count_hint': 'Other (nested) parameters depend on this one',
    'mods.param_branch_add': '↳ nested',
    'mods.param_branch_add_hint': 'Add a parameter active when this one has certain values',
    'mods.param_branch_values_label': 'Active when:',
    'mods.param_branch_warn': 'No value checked — the branch will not be saved',
    'mods.exclusions_button': 'Exclusions',
    'mods.exclusions_button_hint': 'Which parameters and values must never be rolled',
    'mods.exclusions_button_active': 'Excluded: {n}. Click to set up',
    'mods.exclusions_button_none': 'Nothing excluded — all parameters take part in rolls. Click to set up',
    'mods.exclusions_summary_params': 'parameters off: {n}',
    'mods.exclusions_summary_values': 'excluded values: {n} of {total}',
    'mods.exclusions_title': 'Randomization exclusions',
    'mods.exclusions_hint': 'Mod "{name}". Check whole parameters or single values that must never appear in the settings randomizer. The setting is shared — everyone sees it.',
    'mods.exclusions_hint_local': 'Mod "{name}". Check whole parameters or single values that must never appear in the randomizer. Changes are stored in this browser only; "Reset to defaults" restores the shared settings.',
    'mods.exclusions_disable_hint': 'Exclude the whole parameter from randomization',
    'mods.exclusions_disabled': 'not randomized',
    'mods.exclusions_excluded': 'excluded: {n} of {total}',
    'mods.exclusions_value_hint': 'Click to exclude the value from rolls',
    'mods.exclusions_value_back_hint': 'Click to return the value to rolls',
    'mods.exclusions_last_value': 'The last value cannot be excluded — the randomizer needs a choice',
    'mods.exclusions_reset': 'Reset to defaults',
    'mods.exclusions_save_error': 'Failed to save exclusions',
    'mods.param_disabled_short': 'excluded',
    'mods.param_excluded_short': 'excluded: {n}',

    // Auth
    'auth.login_title': 'Log in',
    'auth.signup_title': 'Sign up',
    'auth.login_loading': 'Signing in…',
    'auth.signup_loading': 'Creating…',
    'auth.identifier_placeholder': 'Email or username',
    'auth.password_placeholder': 'Password',
    'auth.password_placeholder_signup': 'Password (min. 6)',
    'auth.show_password': 'Show password',
    'auth.hide_password': 'Hide password',
    'auth.username_placeholder': 'Username (3-32 chars)',
    'auth.email_placeholder': 'Email',
    'auth.no_account': 'No account?',
    'auth.have_account': 'Already have an account?',
    'auth.signup_link': 'Sign up',
    'auth.login_link': 'Log in',
    'auth.error_login': 'Login error: ',
    'auth.error_signup': 'Signup error: ',
    'auth.error_invalid_credentials': 'Wrong email/username or password.',
    'auth.error_email_not_confirmed': 'Email not confirmed. Check your inbox and follow the link in the email, then log in.',
    'auth.error_account_banned': 'Account is banned.',
    'auth.error_username_taken': 'This username is already taken.',
    'auth.error_username_short': 'Username must be at least 3 characters',
    'auth.error_email_invalid': 'Enter a valid email',
    'auth.error_password_short': 'Password must be at least 6 characters',
    'auth.error_identifier_required': 'Enter email or username',
    'auth.error_password_required': 'Enter password',
    'auth.account_created_confirm': 'Account created. Check your email to confirm, then log in.',
    'auth.account_created': 'Account created',
    'auth.captcha_required': 'Please confirm you are not a robot',
    'auth.email_note_title': 'Email tip',
    'auth.email_note_text': 'Confirmation emails may not reach some email providers. We recommend registering with a foreign mailbox, e.g. Gmail.',
    'auth.forgot_link': 'Forgot password?',
    'auth.forgot_title': 'Reset password',
    'auth.forgot_hint': 'Enter your email or username — we will send a password reset link.',
    'auth.forgot_button': 'Send link',
    'auth.forgot_sent': 'If the account exists, a password reset link has been emailed. Check your inbox.',
    'auth.reset_title': 'New password',
    'auth.reset_button': 'Save password',
    'auth.password_repeat_placeholder': 'Repeat password',
    'auth.error_password_mismatch': 'Passwords do not match',
    'auth.reset_invalid': 'The link is invalid or has expired.',
    'auth.reset_success': 'Password changed. You can log in now.',
    'auth.link_expired': 'The link has expired or was already used. Request a new one via "Forgot password?".',
    'auth.link_invalid': 'The link is invalid. Please request a new one.',

    'nav.leaderboard': 'Rating',

    'nav.tools': 'Tools',
    'tools.title': 'Tools',
    'tools.hero_eyebrow': 'FFA League · Arsenal',
    'tools.hero_desc':
        'Everything for preparing and broadcasting a match: map and mod roll, winner prediction, match flow and the stream overlay.',
    'tools.open': 'Open',
    'tools.tag_match': 'Scenario',
    'tools.tag_predict': 'Analytics',
    'tools.tag_random': 'Random',
    'tools.tag_streamer': 'Stream',
    'tools.streamer_desc':
        'OBS overlay: player cards, display settings and the overlay link.',
    'tools.match_title': 'Match manager',
    'tools.match_desc':
        'Interactive match flow: roll a map and mod, gather the players, run the prediction, launch streamer mode with pinned players and add the game to the list.',
    'tools.match_stage_map': 'Map and mode',
    'tools.match_stage_players': 'Players',
    'tools.match_stage_predict': 'Winner prediction',
    'tools.match_stage_stream': 'Streamer mode',
    'tools.match_skip': 'Skip stage',
    'tools.match_skip_back': 'Bring the roll back',
    'tools.match_skipped': 'Stage skipped — map and mod are ignored.',
    'tools.match_no_pick': 'Press “Spin!” to roll a map and mod.',
    'tools.match_open_streamer': 'Open streamer mode',
    'tools.match_streamer_hint':
        '{n} players will be pinned at the top of the list in streamer mode — cards within reach.',
    'tools.match_streamer_no_players':
        'Add players — they will be pinned at the top of the list in streamer mode.',
    'tools.match_preset': 'Preset',
    'tools.match_preset_all': '— all maps and mods —',
    'tools.match_preset_maps': 'Maps in the preset',
    'tools.match_preset_mods': 'Mods in the preset',
    'tools.match_preset_empty': 'The preset has no maps or mods.',
    'tools.match_preset_show': 'Show preset pool',
    'tools.match_preset_hide': 'Hide preset pool',
    'tools.match_preset_dynamic': 'By type and players',
    'tools.match_preset_any_count': 'any player count',
    'tools.match_reset': '⟲ Reset',
    'tools.match_reset_confirm': 'Reset players, the roll, the map/mod pool and the preset?',
    'tools.match_stage_add_game': 'Add game',
    'tools.match_stage_pool': 'Match pool',
    'tools.match_pool_hint':
        'Click a map or mod to cross it out for this match only — the preset and the lists stay unchanged.',
    'tools.match_pool_mods': 'Mods',
    'tools.match_pool_maps': 'Maps',
    'tools.match_settings': 'Configure',
    'tools.match_game_date': 'Date and time',
    'tools.match_add_game': 'Add to the games list',
    'tools.match_add_game_hint':
        '{n} players, the map and the mod will be filled into the new game form.',
    'tools.match_add_game_no_players':
        'Add at least two players — they will be filled into the new game form.',
    'tools.match_add_game_forbidden':
        'Only moderators and admins can add games.',
    'tools.match_add_game_replay_hint':
        'Game already played? Import the replay or drop the .SC2Replay file anywhere on the page — players, map and date will be filled in automatically.',
    'tools.match_replay_title': 'Import from replay',
    'tools.match_replay_choose': 'Choose replay file',
    'tools.match_replay_applied':
        'Match filled from the replay: players, map and date. Please review the data.',
    'tools.match_replay_drop_hint':
        'Players, map and date will be filled in automatically (.SC2Replay)',
    'tools.predict_title': 'Winner prediction',
    'tools.predict_desc':
        'Pick the participants and optionally a map and a mod — we will estimate likely places and win chances based on game history.',
    'tools.predict_players': 'Players',
    'tools.predict_run': 'Calculate',
    'tools.predict_results': 'Result',
    'tools.predict_chart_title': 'Win chances',
    'tools.predict_details_title': 'What the chances are based on',
    'tools.predict_random': 'Reroll winner',
    'tools.predict_random_hint':
        'Draw a preliminary winner from the calculated chances (higher chance means a bigger probability).',
    'tools.predict_random_winner': 'Preliminary winner',
    'tools.predict_placeholder_title': 'Your prediction appears here',
    'tools.predict_placeholder_text':
        'Add the players and hit “Calculate” — we will show win chances and expected places.',
    'tools.predict_placeholder_loading': 'Calculating…',
    'tools.predict_need_two': 'Select at least two players.',
    'tools.predict_error': 'Failed to calculate prediction:',
    'tools.result_place': 'Place',
    'tools.result_player': 'Player',
    'tools.result_win_chance': 'Win chance',
    'tools.result_context': 'Context',
    'tools.result_avg_place': 'avg place',
    'tools.result_map_games': 'on map',
    'tools.result_mod_games': 'with mod',
    'tools.race_none': 'Race not selected',
    'tools.map_not_specified': 'Not specified',
    'tools.result_map': 'on map',
    'tools.result_mod': 'with mod',
    'tools.reset': 'Reset',
    'tools.random_title': 'Map & mode picker',
    'tools.random_desc': 'Random map and mod for the next game. Exclude the ones you do not want — your choice is saved in the browser.',
    'tools.random_spin': 'Spin!',
    'tools.random_map': 'Map',
    'tools.random_mod': 'Mod',
    'tools.random_pool_maps': 'Map pool',
    'tools.random_pool_mods': 'Mod pool',
    'tools.random_all': 'All',
    'tools.random_none': 'None',
    'tools.random_empty': 'Pool is empty',
    'tools.random_hint': 'Click a name to exclude it from the draw or bring it back.',
    'tools.random_load_error': 'Failed to load maps and mods',
    'tools.random_no_mod': 'No mod',
    'tools.random_search': 'Search map…',
    'tools.random_search_mod': 'Search mod…',
    'tools.random_players': 'Players: {n}',
    'tools.random_type_toggle': 'Whole group: include/exclude all maps of this type',
    'tools.random_players_toggle': 'Whole group: include/exclude all maps with this player count',
    'tools.random_search_by_type': 'Type',
    'tools.random_search_by_players': 'Players',
    'tools.random_filters_title': 'List filter',
    'tools.random_filters_note': 'only shows and hides rows — the draw is not affected',
    'tools.random_filters_reset': '✕ Reset',
    'tools.random_filters_reset_hint': 'Reset search and list filters',
    'tools.random_section_reset': 'Clear all settings in this section',
    'tools.random_groups_reset': 'Exclude every map from the draw',
    'tools.random_groups_title': 'Include in the draw',
    'tools.random_groups_note': 'click a group to include or exclude it entirely',
    'tools.random_groups_legend': '✓ whole group · ◐ partial · ✕ excluded',
    'tools.random_types_format': 'Format',
    'tools.random_types_teamformat': 'Team format',
    'tools.random_formats_toggle': 'Whole group: include/exclude all maps of this format',
    'tools.random_types_style': 'Game style',
    'tools.random_types_league': 'League',
    'tools.random_types_players': 'Players',
    'tools.random_tree_toggle': 'Collapse or expand the branch',
    'tools.random_shown': 'Showing {shown} of {total}',
    'tools.random_all_visible': 'All visible',
    'tools.random_none_visible': 'None of visible',
    'tools.random_all_hint': 'Applies to the visible list: search and filter are taken into account',
    'tools.random_no_matches': 'Nothing found — change the search or filter',
    'tools.random_group_selected': 'Selected',
    'tools.random_group_others': 'Others',
    'tools.random_view_label': 'View',
    'tools.random_view_list': 'List',
    'tools.random_view_grid': 'Tiles',
    'tools.random_skip_unbalanced': 'Skip unbalanced',
    'tools.random_skip_unbalanced_hint': 'Do not roll maps marked as unbalanced',
    'tools.random_overlay': 'Show on OBS overlay',
    'tools.random_overlay_hint': 'The spin and its result appear on the OBS overlay (link in Streamer mode). Login required.',
    'tools.overlay_no_source': 'No OBS overlay source is connected — the roll and prediction will go nowhere. Open the link from Streamer mode in OBS (Browser Source) and enable the source.',
    'tools.overlay_no_source_short': 'OBS not connected',
    'tools.random_unbalanced_badge': 'Unbalanced',
    'tools.random_mod_settings': 'Mod settings',
    'tools.random_settings_reroll': '🎲 Reroll',
    'tools.random_settings_fixed': 'fixed',
    'tools.random_settings_off': 'Not randomized',
    'tools.random_settings_off_hint': 'Click to enable and randomize',
    'tools.random_settings_presets': 'Presets:',
    'tools.random_settings_mode_random': '🎲 Random',
    'tools.random_settings_mode_preset': 'Preset',
    'tools.random_settings_mode_hint': 'Where setting values come from: a random roll or a random preset',
    'tools.preset_title': 'Presets',
    'tools.preset_current': '— current selection —',
    'tools.preset_league_maps': 'All FFA League maps',
    'tools.preset_shared': 'Shared',
    'tools.preset_personal': 'Mine',
    'tools.preset_name': 'Preset name',
    'tools.preset_scope_personal': 'Personal',
    'tools.preset_scope_shared': 'Shared',
    'tools.preset_save_new': 'Save as new',
    'tools.preset_update': 'Save changes',
    'tools.preset_reset': 'Discard changes',
    'tools.preset_delete': 'Delete',
    'tools.preset_delete_confirm': 'Delete preset “{name}”?',
    'tools.preset_modified': 'modified',
    'tools.preset_login_hint': 'Log in to save your own presets',
    'tools.preset_exists': 'A preset with this name already exists',
    'tools.preset_error': 'Error:',
    'tools.preset_saved': 'Saved',
    'tools.preset_subtitle':
        'A preset sets the map and mod pool for the draw — no need to switch things off by hand.',
    'tools.preset_quick': 'Quick pick',
    'tools.preset_empty_hint':
        'No presets yet — build the pool below and save it to reuse with one click.',
    'tools.preset_attention': 'Pick a preset — otherwise the draw uses the whole pool.',
    'tools.preset_editor_title': 'Editing',
    'tools.preset_editor_hint':
        'Save the current map and mod selection as a new preset or update the selected one.',
    'tools.map_add_placeholder': 'New map',
    'tools.map_add_alt_placeholder': 'Alt. name (optional)',
    'tools.map_add': '+ Add map',
    'tools.map_exists': 'This map already exists',
    'leaderboard.title': 'Rating',
    'leaderboard.empty': 'No games played yet',
    'leaderboard.games': 'Games',
    'leaderboard.wins': 'Wins',
    'leaderboard.winrate': 'Winrate',

    'games.new_map_prompt': 'New map name:',
    'games.new_map_error': 'Failed to create map: ',
    'games.map_search_placeholder': 'Search map…',
    'games.edit_map': 'Edit map',
    'games.map_alt_placeholder': 'Alt. name (client locale)',
    'games.map_alt_save': 'Save alternative name',
    'games.map_alt_saved': 'Saved',
    'games.map_alt_error': 'Failed to save alternative name',
    'games.field_map_type': 'Map type',
    'games.field_map_types': 'Map types',
    'games.map_type_saved': 'Types saved',
    'games.map_type_error': 'Failed to save map types',
    'games.map_players_placeholder': 'Players',
    'games.map_players_saved': 'Player count saved',
    'games.map_players_error': 'Failed to save player count',

    // Map types
    'map_type.regular': 'Normal',
    'map_type.team': 'Team',
    'map_type.cb': 'CB',
    'map_type.micro': 'Micro',
    'map_type.macro': 'Macro',
    'map_type.hybrid': 'Hybrid',
    'map_type.ffa_league': 'FFA League',

    // Team map format
    'map_format.2v2': '2v2',
    'map_format.3v3': '3v3',
    'map_format.4v4': '4v4',
    'map_format.2v2v2': '2v2v2',
    'map_format.2v2v2v2': '2v2v2v2',
    'games.map_deleted': 'map deleted',
    'games.map_deleted_hint': 'This map is marked as deleted — fix the game record',
    'games.map_deleted_warning': 'Map "{name}" was deleted. Pick another map or restore it in the admin panel.',
    'games.replay_map_not_found': 'Map "{name}" was not found. It may be the same map under a name in another language. Add a name variation to the selected map (field below) or create a new map.',
    'games.replay_map_create': 'Create map from replay',
    'games.new_mod_prompt': 'New mod name:',
    'games.new_mod_error': 'Failed to create mod: ',

    'games.filter_format': 'Format',
    'games.filter_host': 'Host',
    'games.filter_map': 'Map',
    'games.filter_mod': 'Mod',
    'games.filter_player': 'Player',
    'games.filter_all': 'All',
    'games.filter_reset': 'Reset filters',
    'games.click_to_filter': 'Click to filter',

    // Players
    'who.followers': 'Followers',
    'who.likes': 'Liked by',
    'who.dislikes': 'Disliked by',
    'who.none': 'nobody yet',
    'players.title': 'Players',
    'players.search_placeholder': 'Search by name or aka…',
    'players.view_table': 'Table',
    'players.view_cards': 'Cards',
    'players.view_tierlist': 'Tier list',
    'players.tierlist_no_tier': 'No tier',
    'players.tierlist_rater_label': "Player's tier list",
    'players.tierlist_rater_placeholder': 'Choose a player…',
    'players.tierlist_rater_empty': "This player hasn't set any tiers yet",
    'players.tierlist_rater_error': "Failed to load the player's tier list",
    'players.add_placeholder': 'New player name',
    'players.add_button': 'Add',
    'players.review_all': 'Rate all',
    'players.no_players': 'No players yet',
    'players.nothing_found': 'Nothing found',
    'players.loading': 'Loading…',
    'players.load_error': 'Failed to load players: ',
    'players.delete_confirm': 'Delete player "{name}"?\n\nAll their ratings ({count}) will be permanently deleted.',
    'players.delete_error': 'Failed to delete: ',
    'players.sort_by': 'Sort by',
    'players.sort_asc': 'Ascending',
    'players.sort_desc': 'Descending',

    'players.col.elo': 'MMR',

    // Table columns
    'players.col.name': 'Player',
    'players.col.race': 'Race',
    'players.col.races': 'Races',
    'players.col.stats': 'Stats',
    'players.col.total': 'Total',
    'players.col.activity': 'Activity',

    // Stats
    'stat.adaptiveness': 'Adaptability',
    'stat.aggression': 'Aggression',
    'stat.turtle': 'Turtle',
    'stat.variety': 'Variety',
    'stat.survival': 'Survival',
    'stat.greed': 'Greed',

    // Hall of fame
    'hall.title': 'Hall of Fame',
    'hall.subtitle': 'Best and worst per stat (2+ ratings)',
    'hall.empty': 'Not enough ratings yet',
    'hall.high': 'Highest',
    'hall.low': 'Lowest',
    'hall.adaptiveness.high': 'Most adaptive',
    'hall.adaptiveness.low': 'Most rigid',
    'hall.aggression.high': 'Most aggressive',
    'hall.aggression.low': 'Most peaceful',
    'hall.turtle.high': 'Most defensive',
    'hall.turtle.low': 'Most pushing',
    'hall.variety.high': 'Most diverse',
    'hall.variety.low': 'Most one-track',
    'hall.survival.high': 'Most resilient',
    'hall.survival.low': 'Most fragile',
    'hall.greed.high': 'Greediest',
    'hall.greed.low': 'Most selfless',

    // Player description (flavor text, restrained tone). Variants are separated by `||`.
    'player.desc_title': 'Dossier',
    'player.desc.race.terran':
        'A Terran to the core: discipline, a polished plan, and a healthy distrust of improvisation.||' +
        'A classic Terran — solid rear lines and order matter more than haste.||' +
        'The Terran school: they trust preparation more than luck.',
    'player.desc.race.zerg':
        'A child of the swarm: where others count units, they count possibilities.||' +
        'Zerg to the tips of their claws — momentum, economy, and the gift of being everywhere at once.||' +
        'A typical representative of the swarm: patience, numbers, and unexpected attacks.',
    'player.desc.race.protoss':
        'Protoss to the fingertips: technology, clean lines, and impeccable posture.||' +
        'A proper Protoss — expensive units and a firm belief in their own superiority.||' +
        'The Protoss school: they value quality over quantity.',
    'player.desc.race.random':
        'A disciple of chance: even they are not always sure which race they are on.||' +
        'Random is their element: the rival is confused, and that suits both sides.||' +
        'They pick a random race — or so they claim.',
    'player.desc.race.multi':
        'A generalist: several races under the belt, so predicting them is no easy task.||' +
        'A master of all races — which hand to play today is decided by mood.||' +
        'They play several races, and it does the rivals no favors.',
    'player.desc.race.unknown':
        'A figure with no distinguishing marks: the rivals have yet to gather a file on them.||' +
        'A dark horse — neither the race nor the style is really known.||' +
        'Little is known about them, which is intriguing in itself.',
    'player.desc.strong.adaptiveness':
        'Flexibility is their strength: they adapt to any rival and rewrite the plan as the game unfolds.||' +
        'They rebuild quickly when the situation demands it and rarely get stuck on one idea.||' +
        'Adaptability is their calling card: against any matchup they find a workable answer.||' +
        'They read the game on the fly and change strategy before it turns into a disaster.',
    'player.desc.strong.aggression':
        'Pressure is their style: they do not wait for the right moment, they try to create it.||' +
        'They prefer to set the pace and keep the rival tense from the opening minutes.||' +
        'Aggression is their natural habitat: you cannot relax next to them.||' +
        'They like the initiative and are not afraid of early trades.',
    'player.desc.strong.turtle':
        'Defense is their element: they turn a base into a fortress and wait for mistakes.||' +
        'They favor a solid rear and are in no hurry to leave familiar ground.||' +
        'Patient defensive play is their signature: let someone else rush.||' +
        'They know how to wait and capitalize on others\' mistakes without forcing things.',
    'player.desc.strong.variety':
        'Unpredictability is their trump card: their builds are nearly impossible to read.||' +
        'They dislike repeating themselves and play almost every game differently.||' +
        'Variety is their motto: all the rivals can do is be surprised.||' +
        'They improvise and often find unconventional moves.',
    'player.desc.strong.survival':
        'They are tenacious: the last to fall, surviving where others have already given up.||' +
        'Survival is their strength: they are hard to finish off in the thick of the brawl.||' +
        'They last longer than expected, and it often changes the outcome.||' +
        'Endurance is their quiet edge: they are rarely the first to go out.',
    'player.desc.strong.greed':
        'Resources are their passion: they will always take an extra base if given the chance.||' +
        'Economy sometimes matters more to them than battles: they like to grab a little extra.||' +
        'They never miss a chance to expand and squeeze the most out of the map.||' +
        'Without a couple of extra bases the game seems bland to them.',
    'player.desc.weak.adaptiveness':
        'Admittedly, they are in no hurry to change plans mid-game — improvisation does not come easily.||' +
        'Admittedly, if something goes off-script, they will push the old plan rather than rebuild.||' +
        'Admittedly, flexibility is not their strong suit: a new situation throws them off a little.||' +
        'Admittedly, they struggle when the situation shifts abruptly.',
    'player.desc.weak.aggression':
        'Admittedly, aggression is not their thing: they would rather wait even when the moment calls for attack.||' +
        'Admittedly, they dislike applying pressure and often hand the initiative to the rival.||' +
        'Admittedly, they take the initiative reluctantly — reacting suits them better than attacking.||' +
        'Admittedly, early trades do not appeal: they prefer to play it safe.',
    'player.desc.weak.turtle':
        'Admittedly, holding the line to the end does not always work — under pressure the defense sometimes cracks.||' +
        'Admittedly, resilience on defense is not their forte, and they do not always withstand the onslaught.||' +
        'Admittedly, digging in properly does not always happen.||' +
        'Admittedly, they hold up poorly under a long siege.',
    'player.desc.weak.variety':
        'Admittedly, creativity can be a struggle: a couple of favorite pushes are already familiar.||' +
        'Admittedly, they are not shy about repeating themselves: if it works, why change it.||' +
        'Admittedly, their imagination sometimes fails them and the patterns become predictable.||' +
        'Admittedly, their set of ideas is small, and the rivals noticed long ago.',
    'player.desc.weak.survival':
        'Admittedly, reaching the final is rare — in the thick of the brawl they usually go out early.||' +
        'Admittedly, surviving the meat grinder is not their talent.||' +
        'Admittedly, they lack the stamina for the long haul.||' +
        'Admittedly, they handle drawn-out games worse than they would like.',
    'player.desc.weak.greed':
        'Admittedly, they are shy about grabbing extra resources: economy is not their religion.||' +
        'Admittedly, they hold on tightly to what is theirs, but retaking someone else\'s never quite appeals.||' +
        'Admittedly, greed is not their thing: they would rather finish with what they have.||' +
        'Admittedly, they treat expansion without much enthusiasm.',
    'player.desc.trait.adaptiveness': 'flexibility',
    'player.desc.trait.aggression': 'aggression',
    'player.desc.trait.turtle': 'solid defense',
    'player.desc.trait.variety': 'creativity',
    'player.desc.trait.survival': 'endurance',
    'player.desc.trait.greed': 'the hunger for resources',
    'player.desc.also_strong': 'And {trait}, all in all, is not foreign to them either.||On top of that, {trait} is in good shape too.||And {trait} is a nice bonus to the picture.',
    'player.desc.also_weak': 'While {trait} does lag behind somewhat.||Though {trait} clearly falls short.||Only {trait} lets them down.',
    'player.desc.activity.high':
        'They are a constant presence in the lobby — FFA is clearly more than a hobby to them.||' +
        'They are almost always in game: it feels like the lobby is empty without them.||' +
        'You can find them in the lobby at almost any hour.||' +
        'Their activity is high: they rarely skip a good evening.',
    'player.desc.activity.mid':
        'They show up in the lobby regularly: never gone for long, yet not living in the game around the clock.||' +
        'They check in steadily — just enough not to fall behind.||' +
        'They play with enviable regularity, but without fanaticism.||' +
        'Their appearance in the lobby is a familiar sight, if not a daily one.',
    'player.desc.activity.low':
        'They drop by now and then: a couple of games and then vanish again.||' +
        'You do not meet them in the lobby often, so each game with them is nearly an event.||' +
        'They appear rarely but reliably remind everyone they exist.||' +
        'They play in bursts, between other things.',
    'player.desc.skill.high':
        'The results keep up with the style: clearly more wins than excuses.||' +
        'They play confidently, and the table confirms it.||' +
        'Their results speak for themselves.||' +
        'They consistently get what they want — the rivals will confirm.',
    'player.desc.skill.mid':
        'The results sit at a solid average: no fanfare, but no reason to panic either.||' +
        'The outcomes are even: neither soaring nor falling.||' +
        'They hold the middle ground and give no reason to write them off.||' +
        'A consistently average result — reliable, if not flashy.',
    'player.desc.skill.low':
        'The results are modest for now, but the potential is clearly there.||' +
        'Wins do not come easily: experience and tough competition take their toll.||' +
        'The results are not impressive yet, but there is plenty of room to grow.||' +
        'For now they are gaining experience rather than racking up wins.',
    'player.desc.no_games': 'They have hardly been seen in action — for now the legend mostly exists on paper.',
    'player.desc.no_ratings': 'No ratings have been given yet, so we have to judge by deeds rather than by stories.',

    // Player card
    'player.votes': 'Votes',
    'player.vote': 'Vote',
    'player.total': 'Total',
    'player.back': '← Back',
    'player.rating_edit': '✎ Edit my rating',
    'player.rating_add': '+ Add rating',
    'player.rating_delete': '🗑 Delete my rating',
    'player.rating_delete_confirm': 'Delete your rating? This action cannot be undone.',
    'player.rating_save': 'Save',
    'player.rating_saving': 'Saving…',
    'player.rating_save_error': 'Failed to save rating: ',
    'player.rating_delete_error': 'Failed to delete: ',
    'player.aka_edit': 'Aka',
    'player.aka_title': 'Alternative names for "{name}" (comma-separated):',
    'player.aka_save_error': 'Failed to save: ',
    'player.name_edit': '✎ Name',
    'player.name_title': 'New name for "{name}":',
    'player.name_empty_error': 'Name cannot be empty',
    'player.name_save_error': 'Failed to rename: ',
    'player.login_to_rate': 'Log in to rate players.',
    'player.rating_disabled': 'An administrator has disabled rating submissions for your account.',
    'player.login_link': 'Log in',
    'player.edit_title': 'Editing rating',
    'player.edit_save': 'Save',
    'player.edit_cancel': 'Cancel',
    'player.next_player': 'Next',
    'player.card_mode_average': 'Average',
    'player.card_mode_mine': 'Mine',
    'player.card_mode_ghost': 'By GHOST',
    

    // Ratings
    'ratings.title': 'User ratings',
    'ratings.empty_anonymous': 'Log in to see who rated the player and how.',
    'ratings.empty': 'No one has rated this player yet.',
    'ratings.col.user': 'User',
    'ratings.link_to_player_title': 'Go to player',
    'ratings.col.race': 'Race',
    'ratings.col.tier': 'Tier',
    'player.tier_title': 'Tier',
    'player_card.place_held': '{n} d',
    'player_card.place_held_today': 'today',
    'player.tier_unset': 'Not set',
    'player.tier_notice': 'You can now set a player tier when rating. Update your rating and add a tier to show how strong the player is.',
    'ratings.given_title': 'Ratings given by player',
    'ratings.given_empty': 'This player has not rated anyone yet.',
    'ratings.col.player': 'Player',
    'ratings.excluded': 'not counted',
    'ratings.excluded_title': 'This rating is not counted in the player\'s average rating',
    'ratings.admin_delete': 'Delete rating (admin)',
    'ratings.admin_delete_confirm': 'Delete this rating?',
    'ratings.admin_delete_error': 'Failed to delete rating',
    'ratings.admin_delete_all': 'Delete all',
    'ratings.admin_delete_all_confirm': 'Delete all ratings of this player?',
    'ratings.admin_delete_all_given_confirm': 'Delete all ratings given by this player?',

    // Footer
    'footer.made_by': 'Made by',
    'footer.inspired_by': 'Inspired by',
    'footer.discord_copy': 'Copy tag',

    // Control
    'control.title': 'OVERLAY CONTROL',
    'control.connected': 'Connected',
    'control.disconnected': 'Disconnected',
    'control.overlay_off': 'Overlay is not connected',
    'control.overlay_off_hint': 'The OBS source is not connected to the server, so cards and widgets will not appear. Copy the link above into OBS (Browser Source) and enable the source. If you regenerated the token, update the link in OBS. The "Open" button shows the reason when the token is the problem.',
    'control.overlay_sources': 'OBS sources:',
    'control.obs_panel_title': 'OBS BROWSER SOURCE',
    'control.obs_hint': 'Copy the link below and paste it into OBS as a Browser Source. Keep the source enabled — cards will appear automatically.',
    'control.copy_url': 'Copy',
    'control.open_overlay': 'Open',
    'control.regenerate_token': 'Regenerate',
    'control.regenerate_confirm': 'Regenerate token?\n\nThe old OBS link will stop working. You will need to update it in the OBS source.',
    'control.regenerate_done': 'Done',
    'control.regenerate_error': 'Failed to regenerate token: ',
    'control.copy_error': 'Copy the link manually (Ctrl+C / Cmd+C)',
    'control.players_title': 'PLAYERS',
    'control.players_search': 'Search player…',
    'control.preview_title': 'CARD PREVIEW',
    'control.preview_empty': 'No card is shown',
    'control.preview_error': 'Failed to load preview',
    'control.rated_players': 'With ratings',
    'control.unrated_players': 'Without ratings, with games',
    'control.pinned_title': 'Pinned',
    'control.unpin': 'Unpin',
    'control.pin': 'Pin to top',
    'control.pinned_hint': 'Pin players manually or from the Match manager — they stay on top of the list.',
    'control.pin_mode_label': 'Pin mode',
    'control.pin_mode_hint': 'Shows pin buttons 📌 next to players. When off, the buttons are hidden.',
    'control.show_title': 'SHOW',
    'control.hide_button': '✕ Hide card',
    'control.current_nothing': 'Nothing is shown',
    'control.current_prefix': 'Now: ',
    'control.widget_prefix': 'Widget: ',
    'control.widget_predict': 'Match prediction',
    'control.widget_game': 'Game preview',
    'control.hide_widget': 'Hide widget',
    'control.settings_title': 'SETTINGS',
    'control.animation_label': 'Appearance animation',
    'control.animation_fade': 'Fade',
    'control.animation_slide_left': 'Slide from left',
    'control.animation_slide_right': 'Slide from right',
    'control.animation_slide_up': 'Slide from bottom',
    'control.animation_slide_down': 'Slide from top',
    'control.animation_none': 'No animation',
    'control.autohide_label': 'Automatically hide the card after X seconds',
    'control.autohide_delay_label': 'Hide after (seconds)',
    'control.view_mode_label': 'View mode',
    'control.view_mode_average': 'Average ratings',
    'control.view_mode_personal': 'My ratings',
    'control.view_mode_ghost': 'GHOST rating',
    'control.token_error': 'Failed to load token',
    'control.show_error': 'Failed to show: ',
    'control.hide_error': 'Failed to hide: ',
    'control.settings_error': 'Failed to save settings: ',
    'control.no_players': 'No rated players yet',
    'control.back_to_main': 'Back to main',

    // Admin
    'admin.title': 'ADMIN PANEL',
    'admin.users_title': 'Users',
    'admin.users_search': 'Search by email or username…',
    'admin.players_title': 'Players',
    'admin.players_hint': 'Deleting a player will cascade-delete all their ratings.',
    'admin.players_search': 'Search by name or aka…',
    'admin.grant_mod': 'Grant MOD',
    'admin.revoke_mod': 'Revoke MOD',
    'admin.grant_admin': 'Grant ADMIN',
    'admin.revoke_admin': 'Revoke ADMIN',
    'admin.grant_ghost': 'Grant GHOST',
    'admin.revoke_ghost': 'Revoke GHOST',
    'admin.role_toggle_confirm': 'Role {role} for {email}?',
    'admin.role_toggle_error': 'Failed to change role: ',
    'admin.user_no_username': '— no username —',
    'admin.no_users': 'Nothing found',
    'admin.back_to_main': 'Back to main',
    'admin.rename_button': 'Name',
    'admin.aka_button': 'Aka',
    'admin.delete_button': 'Delete',
    'admin.ratings_empty': 'No ratings',
    'admin.rating_delete_confirm': 'Delete rating by "{user}"?',
    'admin.player_delete_confirm': 'Delete player "{name}"?\n\nAll their ratings ({count}) will be permanently deleted.',
    'admin.registered_at': 'Registered',
    'admin.last_seen': 'Last seen',
    'admin.last_seen_just_now': 'just now',
    'admin.last_seen_minutes': '{n} min ago',
    'admin.last_seen_hours': '{n} hr ago',
    'admin.last_seen_days': '{n} d ago',
    'admin.delete_user': 'Delete account',
    'admin.delete_user_confirm': 'Delete account "{user}"?\n\nAll ratings, roles, and data will be permanently deleted.',
    'admin.delete_user_error': 'Failed to delete: ',
    'admin.disable_rate': 'Exclude from average',
    'admin.enable_rate': 'Include in average',
    'admin.disable_rate_title': 'The user\'s ratings will not count toward players\' average rating (they can still rate and see their ratings)',
    'admin.enable_rate_title': 'The user\'s ratings will count toward the average rating again',
    'admin.disable_rate_confirm': 'Exclude user "{user}" ratings from the players\' average rating? They can still rate and see their ratings.',
    'admin.enable_rate_confirm': 'Include user "{user}" ratings in the players\' average rating again?',
    'admin.rate_toggle_error': 'Failed to change rating inclusion: ',
    'admin.ban_user': 'Ban',
    'admin.unban_user': 'Unban',
    'admin.ban_title': 'Ban account (blocks login and rating)',
    'admin.unban_title': 'Unban account',
    'admin.ban_confirm': 'Ban user "{user}"?\n\nThey will not be able to log in or rate.',
    'admin.unban_confirm': 'Unban user "{user}"?',
    'admin.ban_error': 'Failed to change ban status: ',
    'admin.user_banned': '🔨 Banned',
    'admin.impersonate': '↬ Log in as',
    'admin.impersonate_title': 'Log in as this user',
    'admin.impersonate_confirm': 'Log in as user "{user}"?\n\nYou will be logged into their account (until logout/page refresh).',
    'admin.impersonate_error': 'Failed to log in as user: ',
    'admin.impersonate_banned': 'User is banned — cannot log in.',
    'admin.view_stats': 'Stats',
    'admin.view_stats_title': "View the player's personal stats",
    'admin.set_password': 'Password',
    'admin.set_password_title': 'Set a new password (the old one is unavailable — hashed in Auth)',
    'admin.set_password_confirm': 'Reset password for "{user}"?\n\nA new temporary password will be generated — the old one stops working.',
    'admin.set_password_error': 'Failed to reset password: ',
    'admin.password_show': 'Show password',
    'admin.password_hide': 'Hide password',
    'admin.password_copy': 'Copy password',
    'admin.password_new_hint': 'New temporary password (shown once)',
    'admin.archive_title': 'Deleted ratings archive',
    'admin.archive_search': 'Search by player or user…',
    'admin.archive_total': 'Total deleted ratings in archive: {n}',
    'admin.archive_empty': 'No deleted ratings in the archive yet.',
    'admin.archive_deleted': 'Deleted',
    'admin.archive_restore': 'Restore',
    'admin.archive_restore_all': 'Restore all for user',
    'admin.archive_restore_all_title': 'Restore all deleted ratings of this user',
    'admin.archive_restore_confirm': 'Restore this rating?',
    'admin.archive_restore_all_confirm': 'Restore ALL deleted ratings of this user?',
    'admin.archive_restore_error': 'Failed to restore rating: ',
    'admin.trash_title': 'Deleted games and mods',
    'admin.trash_hint': 'A copy of a game or mod is saved here before it is deleted. "Restore" brings it back (with a new number).',
    'admin.trash_empty': 'No deleted games or mods.',
    'admin.trash_game': 'Game',
    'admin.trash_mod': 'Mod',
    'admin.trash_deleted_by': 'deleted by {name}',
    'admin.trash_restore_confirm': 'Restore "{title}"?',
    'admin.trash_restore_error': 'Failed to restore: ',
    'admin.formats_title': 'Game formats',
    'admin.add_format': '+ Add format',
    'admin.hosts_title': 'Hosts',
    'admin.add_host': '+ Add host',
    'admin.no_formats': 'No formats yet',
    'admin.no_hosts': 'No hosts yet',
    'admin.new_format_prompt': 'New format name:',
    'admin.new_format_error': 'Failed to create format: ',
    'admin.new_host_prompt': 'New host name:',
    'admin.new_host_error': 'Failed to create host: ',
    'admin.delete_format_confirm': 'Delete format "{name}"?\n\nGames with this format will remain but lose the link.',
    'admin.delete_host_confirm': 'Delete host "{name}"?',
    'admin.host_player_none': '— not linked —',
    'admin.host_player_title': 'Linked player (streamer): this player\'s card will show the host panel',
    'admin.elo_weight': 'MMR weight (multiplier)',

    'admin.maps_title': 'Maps',
    'admin.add_map': '+ Add map',
    'admin.maps_search': 'Search by name…',
    'admin.mods_title': 'Mods',
    'admin.add_mod': '+ Add mod',
    'admin.mods_search': 'Search by name…',
    'admin.new_map_prompt': 'New map name:',
    'admin.new_map_error': 'Failed to create map: ',
    'admin.delete_map_confirm': 'Delete map "{name}"?\n\nGames with this map will stay; the map will be marked as deleted in them.',
    'admin.alt_name_placeholder': 'Alt. name (client locale)',
    'admin.map_deleted_badge': 'deleted',
    'admin.map_players_placeholder': 'Players',
    'admin.map_players_title': 'Recommended player count for the map',
    'admin.map_formats_title': 'Team map format (used when the map is marked as "Team")',
    'admin.map_author_placeholder': 'Author',
    'admin.map_link_placeholder': 'Link (URL)',
    'admin.map_image_upload': 'Upload image',
    'admin.map_image_hover': 'Hover to preview; click to replace',
    'admin.map_image_remove': 'Remove image',
    'admin.map_image_remove_confirm': 'Remove the image of map "{name}"?',
    'admin.map_image_error': 'Failed to upload image: ',
    'admin.restore': 'Restore',
    'admin.new_mod_prompt': 'New mod name:',
    'admin.new_mod_error': 'Failed to create mod: ',
    'admin.delete_mod_confirm': 'Delete mod "{name}"?\n\nGames with this mod will remain but lose the link.',
    'admin.titles_title': 'Titles',
    'admin.add_title': '+ Add title',
    'admin.titles_hint': 'The title is shown in the player card (top right) and in the overlay.',
    'admin.titles_empty': 'No titles yet. Add one to award it to players.',
    'admin.new_title_prompt': 'New title name:',
    'admin.new_title_error': 'Failed to create title: ',
    'admin.delete_title_confirm': 'Delete title "{name}"?\n\nThe title will be removed from all players.',
    'admin.title_select_player': '— select a player —',
    'admin.title_add_player': '+ Award',
    'admin.title_unassign': 'Remove title',
    'admin.title_name_en_placeholder': 'English translation (EN locale)',
    'admin.title_name_en_title': 'Shown instead of the name when the site is in English. Empty — falls back to the Russian name.',
    'common.empty': 'empty',

    'title.color_gold': 'Gold',
    'title.color_blue': 'Blue (Terran)',
    'title.color_purple': 'Purple (Zerg)',
    'title.color_yellow': 'Yellow (Protoss)',
    'title.color_gray': 'Gray (Random)',
    'title.color_red': 'Red',
    'title.color_green': 'Green',
    'title.color_cyan': 'Cyan',
    'title.color_orange': 'Orange',
    'title.color_pink': 'Pink',

    'title.size_label': 'Size',
    'title.size_small': 'Small',
    'title.size_medium': 'Medium',
    'title.size_large': 'Large',
    'title.size_xlarge': 'X-Large',

    'admin.link_user': 'Link',
    'admin.link_user_title': 'Link player to user profile',
    'admin.link_user_prompt': 'Email or username to link to player "{name}":',
    'admin.link_user_error': 'Failed to link: ',
    'admin.unlink_user': 'Unlink',
    'admin.unlink_user_title': 'Unlink player from user profile',
    'admin.unlink_user_confirm': 'Unlink player "{name}" from user profile?',
    'admin.linked_player': 'Linked to player',
    'admin.user_linked_player': 'Player: {name}',
    'admin.my_profile': 'My profile',

    'admin.logs_title': 'Action log',
    'admin.logs_search': 'Search by text or user…',
    'admin.logs_filter_all': 'All actions',
    'admin.logs_empty': 'No entries yet',
    'admin.logs_refresh': 'Refresh',
    'admin.logs_actor': 'User',
    'admin.logs_system': 'system',

    'admin.visits_title': 'Site visits',
    'admin.visits_hint': 'Page views are collected automatically on navigation. Bots, the admin area and the overlay control page are not tracked.',
    'admin.visits_days_7': '7 days',
    'admin.visits_days_30': '30 days',
    'admin.visits_days_90': '90 days',
    'admin.visits_views': 'Views',
    'admin.visits_visitors': 'Unique visitors',
    'admin.visits_registered': 'Logged in',
    'admin.visits_top': 'Top pages',
    'admin.visits_log_title': 'Visit log',
    'admin.visits_search': 'Search by page, referrer or UA…',
    'admin.visits_empty': 'No visits yet',
    'admin.visits_anonymous': 'anonymous',
    'admin.visits_direct': 'direct',

    'log.action.signup': 'Sign up',
    'log.action.login': 'Log in',
    'log.action.logout': 'Log out',
    'log.action.player_create': 'Player added',
    'log.action.player_rename': 'Player renamed',
    'log.action.player_aka': 'Player aka changed',
    'log.action.player_delete': 'Player deleted',
    'log.action.player_link': 'Profile linked',
    'log.action.player_unlink': 'Profile unlinked',
    'log.action.rating_create': 'Rating added',
    'log.action.rating_update': 'Rating changed',
    'log.action.rating_delete': 'Rating deleted',
    'log.action.game_create': 'Game added',
    'log.action.game_update': 'Game changed',
    'log.action.game_delete': 'Game deleted',
    'log.action.ref_create': 'Reference added',
    'log.action.ref_update': 'Reference changed',
    'log.action.ref_delete': 'Reference deleted',
    'log.action.role_grant': 'Role granted',
    'log.action.role_revoke': 'Role revoked',
    'log.action.user_delete': 'User deleted',
    'log.action.user_rate_allow': 'Ratings allowed',
    'log.action.user_rate_block': 'Ratings blocked',
    'log.action.user_ban': 'User banned',
    'log.action.user_unban': 'User unbanned',
    'log.action.auth_forgot_password': 'Password reset requested',
    'log.action.auth_reset_password': 'Password reset via email',
    'log.action.rating_restore': 'Rating restored',
    'log.action.preset_create': 'Preset created',
    'log.action.preset_update': 'Preset updated',
    'log.action.preset_delete': 'Preset deleted',
    'log.action.title_create': 'Title created',
    'log.action.title_update': 'Title changed',
    'log.action.title_delete': 'Title deleted',
    'log.action.title_assign': 'Title awarded',
    'log.action.title_unassign': 'Title removed',
    'log.action.user_impersonate': 'Impersonation',
    'log.action.user_set_password': 'Password reset',
    'log.action.user_update_username': 'Name changed',
    'log.action.user_update_email': 'Email changed',
    'log.action.user_update_password': 'Password changed',
    'log.action.reaction_player': 'Player reaction',
    'log.action.reaction_game': 'Game reaction',
    'log.action.reaction_mvp': 'MVP vote',
    'log.action.reaction_map': 'Map vote',
    'log.action.reaction_mod': 'Mod vote',
    'log.action.follow_add': 'Followed player',
    'log.action.follow_remove': 'Unfollowed player',
    'log.action.share_card': 'Shared card',

    'topbar.my_profile': 'My profile',

    // Personal profile (/profile)
    'profile.title': 'Personal profile',
    'profile.username_title': 'Profile name',
    'profile.username_hint': 'The name is shown in the site header and in rating lists.',
    'profile.username_placeholder': 'Name (3-32 chars)',
    'profile.email_title': 'Email',
    'profile.email_hint': 'Site emails are sent here. Changing it requires your current password.',
    'profile.email_placeholder': 'Email',
    'profile.password_title': 'Password',
    'profile.password_hint': 'At least 6 characters. After changing, the old password stops working.',
    'profile.current_password_placeholder': 'Current password',
    'profile.new_password_placeholder': 'New password (min. 6)',
    'profile.repeat_password_placeholder': 'Repeat new password',
    'profile.game_profile_title': 'Game profile',
    'profile.game_profile_hint': 'Your player card with ratings, stats and game history.',
    'profile.game_profile_open': 'Open game profile',
    'profile.game_profile_unlinked': 'No game profile is linked to this account yet. Ask a moderator to link one.',
    'profile.save': 'Save',
    'profile.saving': 'Saving…',
    'profile.saved': 'Saved',
    'profile.error_username_invalid': 'Name: 3-32 chars, latin letters, digits, _, ., -',
    'profile.error_username_taken': 'This name is already taken',
    'profile.error_email_invalid': 'Invalid email',
    'profile.error_email_taken': 'This email is already in use',
    'profile.error_wrong_password': 'Wrong current password',
    'profile.error_password_short': 'Password must be at least 6 characters',
    'profile.error_password_mismatch': 'Passwords do not match',
    'profile.error_generic': 'Could not save changes. Please try again.',

    // Personal stats (/my-stats)
    'topbar.my_stats': 'My stats',
    'mystats.title': 'My stats',
    'mystats.player_title': 'Player stats: {name}',
    'mystats.player': 'Player',
    'mystats.mmr': 'MMR',
    'mystats.rank_value': '#{rank} of {total}',
    'mystats.games': 'Games',
    'mystats.wins': 'Wins',
    'mystats.winrate_short': 'wins',
    'mystats.avg_place': 'Average place',
    'mystats.activity': 'Activity',
    'mystats.game_days': 'Game days',
    'mystats.elo_30d': 'MMR over 30 days',
    'mystats.games_30d': '{n} games in 30 days',
    'mystats.elo_title': 'MMR trend',
    'mystats.elo_hint': 'Based on the last {n} games.',
    'mystats.rhythm_title': 'Play rhythm',
    'mystats.rhythm_hint': 'When you play (MSK): opacity is games, colour is winrate.',
    'mystats.heatmap_cell': '{games} games · {winrate}% wins',
    'mystats.heatmap_wr_low': '0% wins',
    'mystats.heatmap_wr_high': '100% wins',
    'mystats.timeline_title': 'Place timeline',
    'mystats.timeline_hint': 'Last 30 games: higher means a better place. Click opens the game.',
    'mystats.timeline_win': 'win',
    'mystats.timeline_top': 'upper half',
    'mystats.timeline_bottom': 'lower half',
    'mystats.ach_title': 'Achievements',
    'mystats.ach_hint': 'Unlocked as you play.',
    'mystats.ach_done': 'Unlocked',
    'mystats.ach.first_win': 'First win',
    'mystats.ach.games_10': '10 games',
    'mystats.ach.games_50': '50 games',
    'mystats.ach.games_100': '100 games',
    'mystats.ach.streak_5': '5 wins in a row',
    'mystats.ach.streak_10': '10 wins in a row',
    'mystats.ach.winrate_55': '55% winrate',
    'mystats.ach.big_lobby': 'Win in a 6+ lobby',
    'mystats.ach.map_master': '100% on a map',
    'mystats.ach.survivor': '10 top-2 finishes',
    'mystats.ach.night_owl': '10 night games',
    'mystats.ach.all_races': 'All three races',
    'mystats.ach.veteran': '30 game days',
    'mystats.pct_title': 'You in the league',
    'mystats.pct_hint': 'How many players you beat on each metric.',
    'mystats.pct_better': 'better than {percent}%',
    'mystats.pct.mmr': 'MMR',
    'mystats.pct.winrate': 'Winrate',
    'mystats.pct.avg_place': 'Average place',
    'mystats.pct.activity': 'Activity',
    'mystats.pct.games': 'Games',
    'mystats.style_title': 'Playstyle',
    'mystats.style_hint': 'Your average rating vs the league average (1–5).',
    'mystats.style_mine': 'You',
    'mystats.style_league': 'League',
    'mystats.style_votes': '{n} ratings',
    'mystats.style_no_votes': 'No style ratings yet — ask for them on your player card.',
    'mystats.arch_title': 'Player archetype',
    'mystats.arch_hint': 'Built from style ratings and your games: what matters is not the absolute score but your strengths relative to your own profile.',
    'mystats.arch_disclaimer': 'Archetype and tilt are calculated from limited statistics and may be inaccurate.',
    'mystats.arch_match': 'expression',
    'mystats.arch_sources': 'Based on {games} games and {votes} style ratings.',
    'mystats.arch_near': 'Close to: {arch} ({match}%)',
    'mystats.arch_part_style': 'style',
    'mystats.arch_part_games': 'games',
    'mystats.signal.quick_wins': '{percent}% quick wins',
    'mystats.signal.fast_games': '{percent}% games under 15 min',
    'mystats.signal.short_edge': '{percent}% vs own level in short games',
    'mystats.signal.win_length': 'wins average {minutes} min',
    'mystats.signal.long_games': '{percent}% games 25+ min',
    'mystats.signal.late_edge': '{percent}% vs own level in long games',
    'mystats.signal.survives': 'outlasts {percent}% of the lobby',
    'mystats.signal.top2_games': 'top-2 in {percent}% of games',
    'mystats.signal.first_out_games': 'first out in {percent}% of games',
    'mystats.tilt_title': 'Tilt',
    'mystats.tilt_calm': 'Calm',
    'mystats.tilt_tilted': 'Tilting',
    'mystats.tilt.decay': 'Session decay: {value}',
    'mystats.tilt.breaks': 'Skip after a weak game: {value}',
    'mystats.tilt_level.calm': 'Unshakable',
    'mystats.tilt_level.steady': 'Steady',
    'mystats.tilt_level.variable': 'Changeable',
    'mystats.tilt_level.volatile': 'Volatile',
    'mystats.tilt_level.tilted': 'Tilting',
    'mystats.tilt_desc.calm':
      'Holds their place from the first game of the day to the last and keeps playing after a weak game, without long pauses.',
    'mystats.tilt_desc.steady':
      'Sometimes dips late in the day or pauses after a weak game, but quickly returns to their level.',
    'mystats.tilt_desc.variable':
      'Form depends on the day: they may fade by the evening or skip a game or two after a weak one.',
    'mystats.tilt_desc.volatile':
      'Often fades late in the day and takes a long break after a weak game.',
    'mystats.tilt_desc.tilted':
      'Collapses through the day and almost always skips games after a weak one — tilt builds up over the session.',
    'mystats.arch_dist_title': 'Archetypes in the league',
    'mystats.arch_dist_hint': 'Archetype spread: the shape of style matters — strengths relative to each player\'s own profile. Dimmed archetypes are not present in the league yet.',
    'mystats.arch_dist_total': '{n} players counted — those with style ratings or at least 3 games.',
    'mystats.arch_dist_you': 'you',
    'mystats.arch_dist_more': 'Show all archetypes (+{n})',
    'mystats.arch_dist_less': 'Collapse',
    'mystats.arch_axis.aggression': 'Aggression',
    'mystats.arch_axis.greed': 'Economy',
    'mystats.arch_axis.turtle': 'Defense',
    'mystats.arch.usurper.name': 'Usurper',
    'mystats.arch.usurper.desc': 'Uncompromising all-in and early pressure: breaks a neighbour from the first minutes without sparing economy.',
    'mystats.arch.magnate.name': 'Magnate',
    'mystats.arch.magnate.desc': 'Absolute greed: multiplies bases and workers while the neighbours are busy with each other.',
    'mystats.arch.fortress.name': 'Fortress',
    'mystats.arch.fortress.desc': 'A die-hard turtle: floods the base with static defense and waits for opponents to crash into it.',
    'mystats.arch.raider.name': 'Expansionist Raider',
    'mystats.arch.raider.desc': 'Aggressive macro: an early push to knock out a neighbour, take their resources and snowball.',
    'mystats.arch.punisher.name': 'Punisher',
    'mystats.arch.punisher.desc': 'Counter-attacker: holds a solid defense, banks a killer army and strikes at anyone who opens up.',
    'mystats.arch.syndicate.name': 'Syndicate',
    'mystats.arch.syndicate.desc': 'Safe macro: every new base goes up under army and turret cover, cruising into an ideal late game.',
    'mystats.arch.grandmaster.name': 'Grandmaster',
    'mystats.arch.grandmaster.desc': 'All-rounder: plays the situation with no skew towards aggression, economy or defense.',
    'mystats.arch.titan.name': 'Titan',
    'mystats.arch.titan.desc': 'Maximum pressure on every front: attack, economy and defense all at once.',
    'mystats.arch.observer.name': 'Observer',
    'mystats.arch.observer.desc': 'Does not force the pace: plays off the situation without overcommitting to any single vector.',
    'mystats.arch.conqueror.name': 'Conqueror',
    'mystats.arch.conqueror.desc': 'Presses and develops at the same time, without neglecting defense.',
    'mystats.arch.gladiator.name': 'Gladiator',
    'mystats.arch.gladiator.desc': 'Attack and defense both maxed: hits and takes hits, economy comes second.',
    'mystats.arch.vanguard.name': 'Vanguard',
    'mystats.arch.vanguard.desc': 'Aggression is the main tool; everything else follows the situation.',
    'mystats.arch.skirmisher.name': 'Skirmisher',
    'mystats.arch.skirmisher.desc': 'Presses fast without investing in economy or building defenses.',
    'mystats.arch.berserker.name': 'Berserker',
    'mystats.arch.berserker.desc': 'Spends economy on pressure but still keeps the base covered.',
    'mystats.arch.guardian.name': 'Guardian',
    'mystats.arch.guardian.desc': 'Builds economy and defense, attacking only when the moment is right.',
    'mystats.arch.tycoon.name': 'Tycoon',
    'mystats.arch.tycoon.desc': 'Economy is the main trump card; everything else adapts around it.',
    'mystats.arch.warden.name': 'Warden',
    'mystats.arch.warden.desc': 'Defense is the main tool; everything else follows the situation.',
    'mystats.arch.gambler.name': 'Gambler',
    'mystats.arch.gambler.desc': 'No defenses and no pressure: plays on the edge, hoping for tempo.',
    'mystats.arch.citadel.name': 'Citadel',
    'mystats.arch.citadel.desc': 'Strong defense with a modest economy: holds positions and waits for mistakes.',
    'mystats.arch.sentinel.name': 'Sentinel',
    'mystats.arch.sentinel.desc': 'Cautious style: not greedy, but does not push forward either.',
    'mystats.arch.drifter.name': 'Drifter',
    'mystats.arch.drifter.desc': 'Neither pressures nor defends: plays off chance and tempo.',
    'mystats.arch.farmer.name': 'Farmer',
    'mystats.arch.farmer.desc': 'Develops calmly, avoiding aggression and unnecessary risk.',
    'mystats.arch.trencher.name': 'Trencher',
    'mystats.arch.trencher.desc': 'Cautious and defensive without going overboard: holds the base and banks.',
    'archetypes.open_all': 'See all archetypes',
    'archetypes.title': 'All archetypes',
    'archetypes.intro': 'The league\'s 23 archetypes: what each is made of, how many players are in it and who expresses it best. Click a name to open the player card.',
    'archetypes.total': '{n} players counted — those with style ratings or at least 3 games.',
    'archetypes.count': '{n} players · {percent}%',
    'archetypes.level.L': 'low',
    'archetypes.level.M': 'medium',
    'archetypes.level.H': 'high',
    'archetypes.players': 'Representatives',
    'archetypes.no_players': 'Not expressed by anyone yet',
    'archetypes.games': '{n} games',
    'archetypes.empty': 'Archetypes are not calculated yet — more style ratings or games are needed.',
    'mystats.mod.polyglot': 'Polyglot',
    'mystats.mod.chameleon': 'Chameleon',
    'mystats.mod.experimenter': 'Experimenter',
    'mystats.mod.dogmatist': 'Dogmatist',
    'mystats.trait.fast_wins': 'Fast wins — {percent}%',
    'mystats.trait.long_wins': 'Long wins — {minutes} min on average',
    'mystats.trait.short_games': 'Short games — {percent}%',
    'mystats.trait.marathon': 'Marathons 25+ min — {percent}%',
    'mystats.trait.late_boom': 'Better late — +{percent}% vs own level',
    'mystats.trait.early_pressure': 'Presses early — +{percent}% in short games',
    'mystats.trait.survivor': 'Survives to {percent}% of the lobby',
    'mystats.trait.top2': 'Top-2 in {percent}% of games',
    'mystats.trait.top_half': 'Top half — {percent}% of games',
    'mystats.trait.first_out': 'First out — {percent}% of games',
    'mystats.trait.early_risk': 'Eliminated early — {percent}% of games',
    'mystats.trait.consistent': 'Stable places — spread {spread}%',
    'mystats.trait.swingy': 'Swingy — spread {spread}%',
    'mystats.trait.night_owl': 'Night games — {percent}%',
    'mystats.trait.weekend': 'Weekend games — {percent}%',
    'mystats.trait.polyglot': 'Plays {n} races',
    'mystats.trait.mono_race': 'Single race — {race}, {percent}% of games',
    'mystats.trait.specialist': 'Narrow pool — {n} maps',
    'mystats.trait.adaptable': 'Flexible style',
    'mystats.trait.map_spread': 'Pool of {n} maps',
    'mystats.trait.comfort_map': 'Favourite map — {percent}% of games',
    'mystats.trait.streaky': 'Streak up to {n} wins',
    'mystats.trait.big_lobby': '6+ lobby wins — {n}',
    'mystats.trait.active': 'Activity — {n} games in 30 days',
    'mystats.trait.evening': 'Peak form around {hour}:00',
    'mystats.trait.rival_hunter': '{name} slayer — {winrate}% wins',
    'mystats.trait.never_wins': 'No wins in {games} games',
    'mystats.survival_title': 'Survival',
    'mystats.survival_hint': 'How long you last in the lobby and when you win.',
    'mystats.survival_share': 'Lobby share',
    'mystats.survival_top2': 'Top-2 finishes',
    'mystats.survival_avg_win': 'Average win',
    'mystats.lobby_title': 'Lobby size',
    'mystats.lobby_hint': 'Winrate by number of players.',
    'mystats.lobby_size': '{n} players',
    'mystats.lobby_size_plus': '6+ players',
    'mystats.h2h_title': 'Head-to-head',
    'mystats.h2h_hint': 'Who you beat most and who beats you most.',
    'mystats.h2h_best': 'Favorite victim',
    'mystats.h2h_worst': 'Personal coach',
    'mystats.wrapped_title': 'Month recap',
    'mystats.wrapped_hint': 'A short summary of the current month.',
    'mystats.wrapped_empty': 'No games this month yet — play and the recap will appear.',
    'mystats.wrapped_elo': 'MMR this month',
    'mystats.wrapped_map': 'Favorite map',
    'mystats.wrapped_rival': 'Most played rival',
    'mystats.wrapped_best_day': 'Best day',
    'mystats.wrapped_fastest': 'Fastest win',
    'mystats.wrapped_longest': 'Longest win',
    'mystats.records_title': 'Records',
    'mystats.records_hint': 'Extremes across the last {n} games.',
    'mystats.records_days': 'days',
    'mystats.records_games': 'games',
    'mystats.records_break': 'Longest break',
    'mystats.records_max_day': 'Most games in a day',
    'mystats.records_map_streak': 'Map win streak',
    'mystats.records_best_series': 'Best streak',
    'mystats.records_wins_in_row': 'wins in a row',
    'mystats.facts_title': 'Fun facts',
    'mystats.facts_hint': 'Built from your games — the more matches, the more accurate.',
    'mystats.advice_title': 'What to improve',
    'mystats.advice_hint': 'Tips based on your stats. Hints, not verdicts.',
    'mystats.races_title': 'Games by race',
    'mystats.places_title': 'Placement distribution',
    'mystats.maps_title': 'Maps',
    'mystats.map_col': 'Map',
    'mystats.games_col': 'Games',
    'mystats.winrate_col': 'Winrate',
    'mystats.avg_place_col': 'Avg. place',
    'mystats.rivals_title': 'Rivals',
    'mystats.rival_col': 'Opponent',
    'mystats.recent_title': 'Recent games',
    'mystats.all_games': 'All games →',
    'mystats.recent_place': '#{place} of {total}',
    'mystats.min': 'min',
    'mystats.recent_form': 'Form',
    'mystats.form_win': 'Win',
    'mystats.form_loss': 'Loss',
    'mystats.career': 'career',
    'mystats.no_data': 'No data yet',
    'mystats.error': 'Could not load your personal stats.',
    'mystats.unlinked_title': 'No game profile linked',
    'mystats.unlinked': 'This page shows your player stats. Ask a moderator to link a game profile to your account.',
    'mystats.unlinked_admin': 'This user has no linked game profile.',
    'mystats.open_profile': 'Open profile',
    'mystats.open_card': 'Player card',
    'mystats.admin_view': 'Admin view: stats of {name}.',
    'mystats.admin_view_generic': 'Admin view: this user has no linked game profile.',
    'mystats.admin_back': '← Back to admin',
    'mystats.day.1': 'Monday',
    'mystats.day.2': 'Tuesday',
    'mystats.day.3': 'Wednesday',
    'mystats.day.4': 'Thursday',
    'mystats.day.5': 'Friday',
    'mystats.day.6': 'Saturday',
    'mystats.day.7': 'Sunday',
    'mystats.fact.rank': 'You are #{rank} of {total} overall — top {percent}%.',
    'mystats.fact.peak': 'Your peak MMR is {elo}.',
    'mystats.fact.favorite_race': 'You play {race} most often — {games} games with a {winrate}% winrate.',
    'mystats.fact.best_map': 'Best map — {map}: {winrate}% wins over {games} games.',
    'mystats.fact.most_played_map': 'Most games on {map} — {games}.',
    'mystats.fact.favorite_format': 'Favourite format — {format}: {games} games with a {winrate}% winrate.',
    'mystats.fact.favorite_mod': 'You play the {mod} mod most often — {games} games.',
    'mystats.fact.best_day': 'Best weekday — {day}: {wins} wins over {games} games.',
    'mystats.fact.fastest_win': 'Fastest win — {minutes} min.',
    'mystats.fact.longest_win': 'Longest win — {minutes} min.',
    'mystats.fact.rival': 'You meet {name} most often — {games} shared games.',
    'mystats.fact.nemesis': 'Toughest opponent — {name}: {winrate}% wins.',
    'mystats.fact.victim': 'Favourite opponent — {name}: {winrate}% wins.',
    'mystats.fact.best_streak': 'Best streak — {n} wins in a row.',
    'mystats.fact.activity': 'You played on {days} game days.',
    'mystats.fact.avg_place': 'Average placement — {place} of {scale}.',
    'mystats.fact.favorite_hour': 'You play most often around {hour}:00 Moscow time.',
    'mystats.fact.win_streak_now': 'You are on a {n}-win streak.',
    'mystats.fact.loss_streak_now': 'You are on a {n}-loss streak — keep your head up.',
    'mystats.fact.sample': 'Detailed stats are based on the last {n} of {total} games.',
    'mystats.advice.play_more': 'Play a few more games — there is not enough data for reliable conclusions yet.',
    'mystats.advice.low_winrate': 'Your winrate is {winrate}% — try a safer opening: avoid greedy fights and scout more.',
    'mystats.advice.race_weak': 'As {race} your winrate is only {winrate}% over {games} games. Consider practising builds or switching race.',
    'mystats.advice.map_avoid': 'You win only {winrate}% on {map} over {games} games — drop it from your pool or practise it separately.',
    'mystats.advice.avg_place_high': 'Average place {place} — survive to the late game more often: save your army and avoid fighting on two fronts.',
    'mystats.advice.early_elim': 'In {percent}% of games you are eliminated in the bottom half — review your early game and scouting.',
    'mystats.advice.losing_streak': 'A {n}-loss streak. Take a break and start the next game by reviewing a replay.',
    'mystats.advice.winning_streak': 'A {n}-win streak — do not change what works, and write down successful builds.',
    'mystats.advice.low_activity': 'Only {games} games in the last 30 days — your leaderboard activity will drop.',
    'mystats.advice.close_to_rank': 'Only {gap} MMR to rank {rank} — one good game can move you up.',
    'mystats.advice.map_hidden_gem': 'You win {winrate}% on {map} but have only {games} games there — pick it more often.',
    'mystats.advice.balanced': 'Everything looks steady: play regularly and try new maps to keep improving.',

    'players.me_suffix': ' (you)',

    'support.button': 'Support the project',
    'support.card_title': 'Card number (Sber)',
    'support.copied': 'Copied!',
    'support.copy_manually_short': 'Copy manually',

    // Плашка и страница поддержки
    'support.topbar': 'Support',
    'support.banner_text': "The site is non-commercial and times are tough — hosting keeps getting more expensive. If you can, please support the project 🙏",
    'support.banner_cta': 'Support',
    'support.banner_dismiss': 'Dismiss',
    'support.page_title': 'Support the project',
    'support.page_intro': 'SC2 FFA League is a non-commercial project. I, nerdError, run it on pure enthusiasm, without ads or paid subscriptions. Times are tough right now, and the hosting that keeps the site online keeps getting more expensive.',
    'support.page_hosting': 'Site hosting costs 550 ₽ per month. It is the main and essentially the only recurring expense — everything else I do for free.',
    'support.methods_title': 'Ways to support',
    'support.method_card_title': 'Transfer to card (Sber)',
    'support.method_card_desc': 'A direct transfer to a Sberbank card. Any amount works — even a small contribution helps.',
    'support.method_card_copy': 'Copy card number',
    'support.method_da_title': 'DonationAlerts',
    'support.method_da_desc': 'The service supports foreign payments — you can support the project from abroad.',
    'support.method_da_button': 'Open DonationAlerts',
    'support.thanks': 'Thanks for any support! Even a kind word or a repost helps the project a lot.',
    'support.back': '← Back',
    'support.donations_title': 'Thanks to everyone who has supported us',

    'games.team_label': 'Team',
    'games.error_need_two_teams': 'Team format requires at least two teams',
    'games.error_multiple_winner_teams': 'Only one team can be the winner',
    'games.error_incomplete_winner_team': 'All players of the winning team must be marked',
    'games.is_team_label': 'Team game',
    'games.team_size_label': 'Team size',
    'games.team_count_label': 'Number of teams',
    'games.error_team_size_mismatch': 'Player count must be divisible by team size ({size})',
    'games.error_teams_uneven': 'All teams must have the same size ({size})',
    'games.error_teams_incomplete': 'Fill in all slots: {teams} teams of {size} players each.',
    'games.distribute_teams': 'Distribute teams',
    'games.team_short': 'Team',

    'games.clear_form': 'Clear',
    'games.clear_form_confirm': 'Clear the form and not use the last game settings?',
    "games.use_last_game": "Copy previous settings",

    'games.track_elim_label': 'Track elimination order',
    'games.new_player': 'New',
    'games.new_player_prompt': 'New player name:',
    'games.new_player_error': 'Failed to create player: ',
    'games.match_add_forbidden': 'You do not have permission to add games.',
    'games.not_eliminated': 'Not eliminated',
    'games.used': 'used',
    'games.from_replay': 'From replay',
    'games.replay_drop_hint': 'Drop a replay file (.SC2Replay) anywhere on this page',
    'games.replay_dropzone': 'Drop the replay file',
    'games.replay_dropzone_hint': 'The game form will be filled in automatically (.SC2Replay)',
    'games.replay_parsing': 'Parsing replay…',
    'games.replay_parse_error': 'Failed to parse replay: ',
    'games.replay_not_replay': 'Choose a StarCraft II replay file (.SC2Replay)',
    'games.replay_too_large': 'Replay file is too large (25 MB max)',
    'games.replay_prefilled': 'Form filled from replay. Please review before saving.',
    'games.replay_player_unmatched': 'Player not found in the database — pick or create one',
    'games.replay_unresolved': 'Unresolved replay players: {names}',
    'games.replays_button': 'Replay folder',
    'games.replays_title': 'Recent replays',
    'games.replays_hint': 'Replays from the folder, newest first. Click any of them to prefill the game form.',
    'games.replays_parsing': 'Parsing replays: {done} of {total}…',
    'games.replays_done': 'Showing {n} of {total} replays',
    'games.replays_empty': 'No .SC2Replay files found in the folder',
    'games.replays_close': 'Hide list',
    'games.replays_duration': '{n} min',
    'games.replays_players': 'players: {n}',
    'games.replays_map_missing': 'map not found in the catalog',
    'games.replays_add': 'Add game',
    'games.replays_more': 'Show more replays',
    'games.replays_change': 'Change folder',
    'games.replays_reset': 'Reset folder',
    'games.replays_restore_hint': 'Replay folder is saved. Click "Replay folder" to load replays.',
    'games.replays_cached': 'cached',
    'games.replay_score_hint': 'Replay score: {score} (gathered {gathered} + killed {killed} + lost {lost} + alive {army}). Survived: {survived}. The list is sorted by this score.',
    'games.replay_complete': 'Replay is complete: the game was played to the end.',
    'games.replay_complete_places': 'Replay is complete: places were filled in from the replay score — please review before saving.',
    'games.replay_incomplete_no_winner': 'The replay has no explicit winner — the match may have been interrupted. Check the winner and places manually.',
    'games.replay_incomplete_truncated': 'Replay data ends {gap} before the game end — score and eliminations may be incomplete.',
    'games.replay_score_close': 'Neighbouring places are almost tied ({gap} apart) — the estimate is ±2% accurate, so places may be swapped. Check manually.',
    'games.replay_host_matched': 'Host identified from a replay observer: {name}.',
    'games.replay_host_candidates': 'Host not identified. Observers in the replay: {names} — pick the host manually.',
    'games.replay_duplicate': 'This replay was already added as a game on {date}.',
    'games.replay_duplicate_no_date': 'This replay was already added as a game.',
    'games.replay_duplicate_open': 'Open game',
    'games.replays_duplicate': 'game already added: {date}',
    'games.replays_duplicate_no_date': 'game already added',

    'leaderboard.activity': 'Activity',
    'leaderboard.avg_place': 'Avg. place',
    'leaderboard.help_title': 'How the rating works',
    'leaderboard.help_intro': 'We reward active players and those who try to survive in the game as long as possible. Losing is fine. Playing matters.',
    'leaderboard.help_activity': 'Activity points are earned for every game. The more you play, the higher your rating.',
    'leaderboard.help_place': 'Better placement gives more points, worse — fewer.',
    'leaderboard.help_not_elim': 'If you survived to the end but didn\'t win — you get almost the maximum points.',
    'leaderboard.help_winner': 'Winning gives the highest points.',
    'leaderboard.help_early': 'A very early elimination gives minimum points, but still counts as a base — your rating never drops below zero.',
    'leaderboard.help_days': 'Playing on different days is additionally rewarded',
    'leaderboard.help_quality': '"Quality" is the average activity points per game. It shows how effective each individual game is for you.',
    'leaderboard.help_provisional': 'MMR is a strength estimate that accounts for how little you have played. While a player has few games, the rating is computed cautiously: it is pulled toward the starting 1500 and lowered for the "uncertainty" of the result. So one or two lucky games will not put you on top above experienced players — the rating grows and "settles" as games accumulate.',
    'leaderboard.breakdown_title': 'MMR contribution by placement',
    'leaderboard.breakdown_top': '#1–2',
    'leaderboard.breakdown_mid': '#3–6',
    'leaderboard.breakdown_bottom': '#7+',
    'leaderboard.breakdown_unknown': 'No place',
    'leaderboard.breakdown_total': 'Total raw MMR',

    'leaderboard.explain_title': 'How MMR is calculated',
    'leaderboard.explain_button': 'How does MMR work?',
    'leaderboard.explain_intro': 'MMR starts at 1500 and changes after every game. What matters is not the win itself, but the result relative to expectations: your placement in the lobby, the opponents\' ratings and the game format.',
    'leaderboard.explain_how_title': 'How it works — in plain words',
    'leaderboard.explain_how_1': 'MMR compares your result with what was expected. Do better than expected — the rating rises; worse — it falls. What matters is not the win itself, but how well you performed relative to your opponents.',
    'leaderboard.explain_how_2': 'The expectation depends on opponent strength. Losing to stronger players is no shame — you lose little; losing to weaker players is costly. Beating weaker players gives little, beating stronger players gives a lot.',
    'leaderboard.explain_how_3': 'In FFA placement matters too: surviving to the end without winning is almost as valuable as a win (a win is the maximum), while an early elimination is costly.',
    'leaderboard.explain_how_4': 'Lobby size amplifies the effect: in a big game every result weighs more (the N − 1 multiplier), since you beat or lost to more people.',
    'leaderboard.explain_how_5': 'The change per game is capped at ±50: no single game will sink or skyrocket the rating — it moves gradually, over a series of matches.',
    'leaderboard.explain_how_6': 'A cautious adjustment for how little you have played: with few games the rating is cautious — closer to 1500 and lowered by the amount of uncertainty, so a lucky run will not put you on top. As games accumulate, the rating solidifies.',
    'leaderboard.explain_formula_title': 'The formula',
    'leaderboard.explain_e': 'Expectation against each opponent: E = 1 / (1 + 10^((R_opponent − R_you) / 400)), then averaged over all opponents in the game.',
    'leaderboard.explain_delta': 'Change: Δ = K · (N − 1) · (S − E) · format_weight, where K = 32, N is the player count, and the change per game is capped at ±50.',
    'leaderboard.explain_s': 'The result S is derived from placement: win = 1.0; surviving to the end without winning ≈ (N − 2) / (N − 1); elimination = (N − place) / (N − 1).',
    'leaderboard.explain_confidence': 'After recalculation, a cautious adjustment for how little you have played is applied: with few games the rating is pulled toward 1500 and further lowered by the amount of uncertainty. So one or two games will not put you on top.',
    'leaderboard.explain_example_title': 'Example: Emperror vs Seigneur',
    'leaderboard.explain_example_intro': 'Both are roughly equal by raw rating, but Emperror is slightly higher and has played more games — so he ends up 25 MMR above.',
    'leaderboard.explain_row_games': 'Games',
    'leaderboard.explain_row_firsts': 'Finishes 1st',
    'leaderboard.explain_row_raw': 'Raw MMR',
    'leaderboard.explain_row_mmr': 'Final MMR',
    'leaderboard.explain_buckets_note': 'Raw MMR contribution by placement:',
    'leaderboard.explain_place_col': 'Place',
    'leaderboard.explain_delta_col': 'Δ MMR',
    'leaderboard.explain_games_col': 'Games',
    'leaderboard.explain_conclusion': 'The key is the middle bracket. Seigneur loses on average about 16 MMR for every 3rd–6th place finish: his rating is high, expectations are higher, and such placements do not meet them. Emperror\'s middle bracket is roughly at zero. Wins give both a similar boost, but the middle placements decide it. The final 25 MMR gap, adjusted for games played, is effectively "within noise".',
    'leaderboard.explain_reading_title': 'How to read the rating',
    'leaderboard.explain_reading': 'Look not only at the final MMR, but also at the number of games: a player with 30+ matches has a stable rating, a newcomer has a provisional one. Expanding a row shows what the rating is made of (the contribution of placements 1–2, 3–6 and 7+). The most solid rating belongs to those who consistently place high and never fall deep.',
    'leaderboard.expand_hint': 'Click a row to open the progress chart',
    'leaderboard.open_my_chart': 'Open the player chart in the rating',
    'leaderboard.chart_title': 'Player progress',
    'leaderboard.chart_hint': 'MMR, leaderboard place, activity, games and wins over recent matches.',
    'leaderboard.chart_elo': 'MMR',
    'leaderboard.chart_rank': 'Leaderboard place',
    'leaderboard.chart_games': 'Games',
    'leaderboard.chart_wins': 'Wins',
    'leaderboard.chart_empty': 'This player has no games yet',
    'leaderboard.chart_error': 'Failed to load game history',
    'leaderboard.chart_toggle': 'Toggle series',
    'leaderboard.chart_win': 'Win',
    'leaderboard.chart_place': 'Place',
    'leaderboard.chart_team': 'team',

    'leaderboard.mode_all': 'All',
    'leaderboard.team_view_players': 'Individual',
    'leaderboard.team_view_pairs': 'Duos',
    'leaderboard.pair_col': 'Duo',
    'leaderboard.pair_elo': 'Duo MMR',
    'leaderboard.pair_elo_hint': 'A separate rating for the duo, calculated only from their shared games',
    'leaderboard.pair_sum': 'Combined MMR',
    'leaderboard.pair_together': 'Together',
    'leaderboard.pair_empty': 'No team games where both players shared a team yet',
    'leaderboard.pair_chart_title': 'Duo progress',
    'leaderboard.pair_chart_empty': 'This duo has no shared games yet',
    'leaderboard.pair_help_title': 'How the duo standings work',
    'leaderboard.pair_help_intro': 'Here players are paired only when they actually played on the same team.',
    'leaderboard.pair_help_sum': '“Duo MMR” is a separate rating for the pair. It is calculated only from their shared games using the same Elo rules as personal MMR, and is not the sum of the two players’ ratings.',
    'leaderboard.pair_help_together': 'A duo starts at 1500 and moves with the team result: a win raises the rating, a loss lowers it. More shared games make the duo MMR more accurate.',
    'leaderboard.pair_help_games': 'Games, wins, win rate and average place count only matches where both players were on the same team.',
    'leaderboard.pair_help_outro': 'A duo appears after its first shared game; more games make its numbers more reliable.',
'leaderboard.pair_days': 'Game days',
'leaderboard.season_pair_help_rank': 'Season duos are ranked by wins, then fewer games and average place.',
'leaderboard.season_pair_help_days': '“Game days” is the number of season days the duo played together.',
    'leaderboard.filter_30d': 'Last 30 days',
    'leaderboard.mode_solo': 'FFA',
    'leaderboard.mode_team': 'Team FFA',
    'leaderboard.mode_league_s3': 'FFA League (Season 3)',
    'leaderboard.mode_team_s1': '2x2 FFA League (Season 1)',
    'leaderboard.season_help_title': 'Season standings',
    'leaderboard.season_help_text': 'Only games of this format played from September 2026 through the end of 2026 are counted. The main metric is the number of wins.',
    'leaderboard.days': 'Days',
    'leaderboard.quality': 'Quality',
    'leaderboard.rank_up': 'Up {n}',
    'leaderboard.rank_down': 'Down {n}',
    'leaderboard.rank_prev': 'Previous game day: #{n}',
    'leaderboard.rank_new': 'New in the ranking',
    'leaderboard.rank_new_short': 'new',
    'leaderboard.rank_held': '{n} d',
    'leaderboard.rank_held_today': 'today',
    'leaderboard.rank_held_title': 'Holding this place for {n} days',
    'leaderboard.rank_reign': '{n} d on the throne',
    'leaderboard.rank_reign_today': 'took the throne today',
    'leaderboard.rank_reign_title': 'Holding first place for {n} days',
    'leaderboard.aka_hint': 'Show all aka',
    'leaderboard.aka_collapse': 'Collapse aka',

    // Weekly "hall of fame" above the leaderboard
    'leaderboard.hof_title': 'Hall of fame',
    'leaderboard.hof_period_week': 'Week',
    'leaderboard.hof_period_month': 'Month',
    'leaderboard.hof_period_year': 'Year',
    'leaderboard.hof_period': '{from} — {to} · games: {n}',
    'leaderboard.hof_games': 'games: {n}',
    'leaderboard.hof_opponents': 'avg. opponent MMR: {n}',
    'leaderboard.hof_minutes': '{n} min',
    'leaderboard.hof_wins_n': 'wins: {n}',
    'leaderboard.hof_empty': 'No games in this period yet',
    'leaderboard.hof_wins': 'Wins',
    'leaderboard.hof_wins_hint': 'Most wins this week',
    'leaderboard.hof_activity': 'Activity',
    'leaderboard.hof_activity_hint': 'Total activity points this week',
    'leaderboard.hof_climb': 'Rating climb',
    'leaderboard.hof_climb_hint': 'Biggest MMR climb this week: MMR-based rank before and after',
    'leaderboard.hof_mmr': 'MMR gain',
    'leaderboard.hof_mmr_hint': 'Biggest net MMR gain this week (sum of MMR changes)',
    'leaderboard.hof_streak': 'Win streak',
    'leaderboard.hof_streak_hint': 'Longest win streak this week',
    'leaderboard.hof_place': 'Average place',
    'leaderboard.hof_place_hint': 'Best average finishing place this week (3+ games)',
    'leaderboard.hof_days': 'Game days',
    'leaderboard.hof_days_hint': 'How many days of the week the player played',
    'leaderboard.hof_maps': 'All-rounder',
    'leaderboard.hof_maps_hint': 'Most different maps this week',
    'leaderboard.hof_upset': 'Giant slayer',
    'leaderboard.hof_upset_hint': 'Most wins over opponents with a higher average MMR',
    'leaderboard.hof_podium': 'Podium',
    'leaderboard.hof_podium_hint': 'Most top-3 finishes by final place',
    'leaderboard.hof_stability': 'Consistency',
    'leaderboard.hof_stability_hint': 'Most stable finishing positions (smallest spread, 3+ games)',
    'leaderboard.hof_rookie': 'Rookie of the week',
    'leaderboard.hof_rookie_hint': 'Best result among players who played their first league games',
    'leaderboard.hof_sprinter': 'Sprinter',
    'leaderboard.hof_sprinter_hint': 'Fastest win of the week',
    'leaderboard.hof_siege': 'Siege',
    'leaderboard.hof_siege_hint': 'Longest win of the week',
    'leaderboard.hof_daywins': 'Daily wins',
    'leaderboard.hof_daywins_hint': 'Most wins in a single game day',
    'leaderboard.hof_rivals': 'Blood rivals',
    'leaderboard.hof_rivals_hint': 'Pairs of players who faced each other the most',
    'leaderboard.hof_second': 'Always second',
    'leaderboard.hof_second_hint': 'Most second places without a single win',
    'leaderboard.hof_chill': 'Activity drop',
    'leaderboard.hof_chill_hint': 'Biggest drop in activity compared to last week',

    'statistics.title': 'Statistics',
    'statistics.summary_games': 'games played',
    'statistics.summary_players': 'players in the league',
    'statistics.summary_maps': 'maps in rotation',
    'statistics.summary_avg_players': 'avg. lobby size',
    'statistics.summary_hours': 'hours played',
    'statistics.games_title': 'Games by race',
    'statistics.games_desc': 'A race winrate is shown as a ring; below — how the race looks by average place, activity and player count. Bars compare races with each other, numbers are for precision.',
    'statistics.style_title': 'Average style ratings by race',
    'statistics.style_desc': 'Average playstyle of a race across six axes: adaptiveness, aggression, turtle, variety, survival and greed. The further from the centre, the stronger the trait.',
    'statistics.col_race': 'Race',
    'statistics.col_ratings': 'Ratings',
    'statistics.col_players': 'Players',
    'statistics.maps_title': 'Statistics by map',
    'statistics.mods_title': 'Statistics by mod',
    'statistics.hosts_title': 'Statistics by host',
    'statistics.col_name': 'Name',
    'statistics.col_avg_players': 'Avg. players',
    'statistics.col_avg_duration': 'Avg. min',
    'statistics.col_last_game': 'Last game',
    'statistics.show_all': 'Show all',
    'statistics.hide_all': 'Hide',
    'statistics.empty': 'No data yet',
    'statistics.mode_all': 'All time',
    'statistics.mode_now': 'Now',
    'statistics.balance_title': 'Race balance',
    'statistics.balance_desc': 'The power index is calculated over all of a race\'s games from wins (relative to expected, given the player count), placements (including top-2 finishes) and activity. The result is normalised to the field average and amplified for readability: 1.00 is the average, above means stronger, below — weaker.',
    'statistics.balance_desc_now': 'The same power index, but over the last ~60 games — this shows a race\'s current form rather than its all-time average. 1.00 is the field average: above means stronger right now, below — weaker.',
    'statistics.balance_most_imba': 'Most overpowered',
    'statistics.balance_least_imba': 'Weakest',
    'statistics.balance_balanced': 'field average',
    'statistics.balance_weak_side': '← weaker',
    'statistics.balance_imba_side': 'stronger →',
    'statistics.mmr_title': 'Race MMR',
    'statistics.mmr_desc': 'A conditional rating for each race over time. In every game a race gains or loses points depending on whether its players performed above or below the field average (based on placements). The lines show how each race\'s strength changed from match to match; 1500 is the starting point.',
    'statistics.power_title': 'Race imbaness over time',
    'statistics.power_desc': 'The same power index as in "Race balance", shown over time: each point is computed from all games up to that moment, so the rightmost point matches the "All time" balance. The 1.00 dash is the field average: above means more overpowered, below — weaker.',
    'statistics.power_desc_now': 'The same power index, but over a sliding window of the last ~60 games (current form). The rightmost point matches the "Now" balance. The 1.00 dash is the field average: above means more overpowered, below — weaker.',
    'statistics.monthly_games_title': 'Games by month',
    'statistics.monthly_games_desc': 'Number of games played each month.',
    'statistics.race_players_title': 'Race activity by month',
    'statistics.race_players_desc': 'How many unique players of each race played in the league during the month.',
    'statistics.players_count_title': 'Winrate by player count',
    'statistics.players_count_desc': 'A race\'s winrate in lobbies with 4, 5, 6 or more players (the small number under the percentage is the games count). Shows which race does better in big lobbies and which in small ones. Faded cells have fewer than 5 games — pure noise.',
    'statistics.lobby_race_title': 'Own race in the lobby',
    'statistics.lobby_race_desc': 'A race\'s winrate depending on how many of its players are in the same game: one, two, or three and more. Shows whether numbers help the race or usually hurt it.',
    'statistics.lobby_bucket_1': '1 in lobby',
    'statistics.lobby_bucket_2': '2 in lobby',
    'statistics.lobby_bucket_3': '3+ in lobby',
    'statistics.places_title': 'Placement distribution',
    'statistics.places_desc': 'What share of a race\'s games ended on each place (percent of all its games). Unlike average place, it shows the full picture: a couple of wins with many last places, or consistent mid-table finishes.',
    'statistics.col_place': 'Place',
    'statistics.first_elim_title': 'Eliminations by race',
    'statistics.first_elim_desc': 'How often a race is eliminated first and how often it survives to the final (second place). Only games with a known elimination order are counted.',
    'statistics.first_elim_first': 'eliminated first',
    'statistics.first_elim_last': 'reaches the final',
    'statistics.col_favorite_win': 'Favourite wins',
    'statistics.heatmap_title': 'When people play',
    'statistics.heatmap_desc': 'Games played by weekday and hour (MSK). The hour axis starts at 12:00, so 00 sits in the middle and evening/night hours stay together. Brighter means more games in that hour — shows when it is easiest to gather a lobby.',
    'statistics.heatmap_less': 'less',
    'statistics.heatmap_more': 'more',

    "leaderboard.help_outro": "Losing is not scary. The main thing is to play.",

    'games.now': 'Now',

    'games.show_all_players': 'Show all',
    'games.no_winner': 'No winner',
    'games.teams_short': 'teams',
    'games.hide_all_players': 'Hide all',
    'games.place_1': '1st place',
    'games.overlay_show': 'To OBS overlay',
    'games.overlay_show_hint': 'Show the game preview in the OBS overlay for 15 seconds (overlay link is in "Streamer mode").',
    'games.overlay_show_done': '✓',
    'overlay.game_title': 'Game',
    'overlay.status_connected': 'Overlay connected',
    'overlay.status_connecting': 'No connection to the server — reconnecting…',
    'overlay.status_card_error': 'Failed to load the card — retrying…',
    'overlay.status_bad_token': 'The overlay token is invalid. Regenerate it in Streamer mode and update the OBS link.',
    'overlay.status_no_token': 'The overlay link has no token. Copy the link from Streamer mode.',

    // Live streams banner
    'streams.banner_label': 'Live now:',
    'streams.viewers': '{n} viewers',
    'streams.banner_close': 'Hide',
    'streams.ffa_candidate': 'Potential FFA',

    // Streamers (admin)
    'admin.streamers_title': 'Streamers',
    'admin.add_streamer': '+ Add streamer',
    'admin.streamers_hint': 'Links to FFA streamers’ Twitch channels. The site checks who is live and shows a banner at the top of the page.',
    'admin.streamers_empty': 'No streamers added yet.',
    'admin.new_streamer_prompt': 'Streamer channel URL (e.g. https://twitch.tv/nickname):',
    'admin.new_streamer_name_prompt': 'Streamer display name:',
    'admin.streamer_name_required': 'Please provide the streamer name',
    'admin.delete_streamer_confirm': 'Delete streamer "{name}"?',
    'admin.streamer_url_placeholder': 'https://twitch.tv/…',
    'admin.streamer_name_placeholder': 'Streamer name',
    'admin.streamer_test': 'Test',
    'admin.streamer_test_title': 'Always show this stream in the banner — to preview how it looks (ignores Twitch)',
    'admin.streamer_player_placeholder': 'Player (not linked)',
    'admin.streamer_player_hint': 'Links the channel to a player profile: their card gets a “Links” section. Clear the field to unlink.',
    'admin.streamer_save_error': 'Failed to save streamer: ',
    'admin.streamer_delete_error': 'Failed to delete streamer: ',

    'log.action.streamer_create': 'Streamer added',
    'log.action.streamer_update': 'Streamer updated',
    'log.action.streamer_delete': 'Streamer deleted',

    // Donations (admin)
    'admin.donations_title': 'Donations',
    'admin.donations_hint': 'Received donations. Shown as a list at the bottom of the “Support the project” page.',
    'admin.donations_empty': 'No donations added yet.',
    'admin.add_donation': '+ Add donation',
    'admin.donation_name_placeholder': 'Name',
    'admin.donation_amount_placeholder': 'Amount',
    'admin.donation_message_placeholder': 'Message (optional)',
    'admin.donation_required': 'Please provide a name and a positive amount',
    'admin.donation_save_error': 'Failed to save donation: ',
    'admin.donation_delete_error': 'Failed to delete donation: ',
    'admin.delete_donation_confirm': 'Delete donation from "{name}"?',

    'log.action.donation_create': 'Donation added',
    'log.action.donation_update': 'Donation updated',
    'log.action.donation_delete': 'Donation deleted',

    // Engagement
    'recap.title': 'Since your last visit',
    'recap.subtitle': 'While you were away:',
    'recap.games': 'New games',
    'recap.players': 'Players in them',
    'recap.your_games': 'Your games (wins/games)',
    'recap.mmr': 'MMR change',
    'recap.rank': 'Ranking position',
    'recap.notifications': 'Unread notifications',
    'recap.followers': 'New followers',
    'recap.last_game': 'Last game',
    'recap.react_title': 'Games you have not rated yet',
    'recap.react_hint': 'Reactions and “most memorable” — one click away',
    'recap.empty': 'Nothing new yet — but that is easy to fix.',
    'recap.open_games': 'Browse games',
    'recap.dismiss': 'Got it',
    'recap.test': 'Show “while you were away” (test)',

    'follow.button': 'Follow',
    'follow.following': 'Following',
    'follow.count': '{n} followers',
    'follow.login': 'Sign in to follow',
    'follow.hint': 'We will notify you about wins and streams',
    'follow.error': 'Could not update the subscription',
    'follow.unavailable': 'Unavailable',

    'share.button': 'Share',
    'share.download_hint': 'Download PNG image',
    'share.player_title': 'SC2 FFA League · player',
    'share.game_title': 'SC2 FFA League · game',
    'share.game_subtitle': 'Game result',
    'share.downloaded': 'Image saved',
    'share.error': 'Could not share',


    'reactions.title': 'Reactions',
    'reactions.fire': 'Spectacular',
    'reactions.clutch': 'Clutch',
    'reactions.brain': 'Macro',
    'reactions.lucky': 'Lucky',
    'reactions.throw': 'Thrown',
    'reactions.boring': 'Boring',
    'reactions.mvp': 'Most memorable',
    'reactions.mvp_short': 'Memorable',
    'reactions.mvp_hint': 'Who was the most memorable in this game?',
    'reactions.login': 'Sign in to vote',
    'reactions.votes': '{n} votes',
    'reactions.you': 'your vote',
    'reactions.error': 'Could not save the reaction',
    'reactions.unavailable': 'Reactions are not available yet',
    'reactions.todo': '🔥 you have not rated this game yet',

    'player.reactions_title': 'Player vibes',
    'player.reactions_card_title': 'Vibes',
    'player.reactions_total': 'Reactions: {n}',
    'player.reactions_hint': 'Tap everything that fits — multiple allowed',
    'player.reactions_login': 'Sign in to react',
    'player.reactions_verdict': 'League verdict',
    'player.reactions_error': 'Could not save the reaction',
    'player.reaction.love': 'Love him',
    'player.reaction.respect': 'Respect',
    'player.reaction.friend': 'One of us',
    'player.reaction.teacher': 'Teaches me',
    'player.reaction.funny': 'Makes me laugh',
    'player.reaction.danger': 'Dangerous rival',
    'player.reaction.chaos': 'Unpredictable',
    'player.reaction.lucky': 'Lucky',
    'player.reaction.annoy': 'Annoys me',
    'player.reaction.throw': 'Throws games',
    'player.reaction.turtle': 'Drags games',
    'player.reaction.toxic': 'Toxic',

    'challenge.title': 'Weekly challenge',
    'challenge.days_left': '{n} days left',
    'challenge.progress': '{current} of {target}',
    'challenge.done': 'Completed!',
    'challenge.login': 'Sign in to track your progress',
    'challenge.unlinked': 'Link your player profile to track progress',
    'challenge.wins': 'Win {n} games this week',
    'challenge.games': 'Play {n} games this week',
    'challenge.map_win': 'Win on «{map}»',
    'challenge.race_win': 'Win {n} games as {race}',
    'challenge.top2': 'Finish top-2 {n} times',
    'challenge.unique_maps': 'Play on {n} different maps',
    'challenge.streak': 'Win {n} games in a row',
    'challenge.open': 'My stats',

    'notif.follow_win': '{name} won',
    'notif.streamer_live': '{name} is live',
    'notif.open_stream': 'Watch',

    // Loading
    'loading.text': 'LOADING…',
};
